import React, {useEffect, useReducer, useRef, useState} from "react";
import {Checkbox, FormControlLabel, IconButton, MenuItem, TextField, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import moment from "moment-timezone";
import {makeEnterpriseComponent} from "../util/utility";
import EventBus from "../util/EventBus";
import EnterpriseComponent from "../eoec/ec/EnterpriseComponent";
import {prepareBuffer} from "../util/Buffer";
import {Backup, CloudUpload, KeyboardBackspace, TextSnippet} from "@mui/icons-material";
import css_buttons from "../../src/components/Common/css/CCButton.module.scss";
import {Grey6} from "../theme/styles";
import UploadSpreadsheet from "../components/Common/UploadSpreadsheet";
import spinner from "../assets/Spinner-1s-200px.svg";
import {v4 as uuidv4} from "uuid";
import CCButton from "../components/Common/CCButton";
import CCDatePicker from "../components/Common/CCDatePicker";
import {SessionContext, hasCompanyCreatePermission, hasCompanyDeletePermission, hasExportEnlilDataPermission } from "../util/SessionContext";
import EnlilAxiosRestAPI from "../util/EnlilAxiosRestAPI";
import {S3Client} from "@aws-sdk/client-s3";
import {Upload} from "@aws-sdk/lib-storage";

type JobStep = {
    controls: any
}

interface State {
    steps: Array<JobStep>
}

interface Action {
    type: 'ADD'|'MARK_CURRENT'|'CONTROL_UPDATE';
    control: any | undefined;
    step: JobStep | undefined;
    stepIndex: number | undefined;
    formElementId: string | undefined;
}

export function stepReducer(state: State, action: Action) {
    switch (action.type) {
        case 'CONTROL_UPDATE': {
            let _state: State = structuredClone(state);
            _state.steps[action.stepIndex!].controls[action.formElementId!] = action.control;

            // @ts-ignore
            switch(action.formElementId) {
                // @ts-ignore
                case 'Type': {
                    _state.steps[action.stepIndex!].controls["SubType"] = {
                        "value": "", valid: false
                    }
                }
                // @ts-ignore
                // fallthrough
                case 'SubType': {
                    _state.steps[action.stepIndex!].controls["Object"] = {
                        "value": "", valid: false
                    }
                    _state.steps[action.stepIndex!].controls["ExportType"] = {
                        "value": "", valid: false
                    }
                }
                // @ts-ignore
                // fallthrough
                case 'Object': {
                    _state.steps[action.stepIndex!].controls["DocumentType"] = {
                        "value": "", valid: false
                    }
                    _state.steps[action.stepIndex!].controls["DocumentTypeGroup"] = {
                        "value": "", valid: false
                    }
                }
                // @ts-ignore
                // fallthrough
                case 'DocumentType': {
                    _state.steps[action.stepIndex!].controls["DocumentTypeGroup"] = {
                        "value": "", valid: false
                    }
                    _state.steps[action.stepIndex!].controls["Form"] = {
                        "value": "", valid: false
                    }
                }
                // @ts-ignore
                // fallthrough
                case 'Form': {
                    _state.steps[action.stepIndex!].controls["UploadSource"] = {
                        "value": "", valid: false
                    }
                    _state.steps[action.stepIndex!].controls["CalibrationDocumentType"] = {
                        "value": "", valid: false
                    }
                    _state.steps[action.stepIndex!].controls["ARDocumentType"] = {
                        "value": "", valid: false
                    }
                    _state.steps[action.stepIndex!].controls["ARForm"] = {
                        "value": "", valid: false
                    }
                }
                // @ts-ignore
                // fallthrough
                case 'ARDocumentType': {
                    _state.steps[action.stepIndex!].controls["ARForm"] = {
                        "value": "", valid: false
                    }
                }
                // @ts-ignore
                // fallthrough
                case 'ARForm': {
                    _state.steps[action.stepIndex!].controls["UploadSource"] = {
                        "value": "", valid: false
                    }
                }
                // @ts-ignore
                // fallthrough
                case 'CalibrationDocumentType': {
                    _state.steps[action.stepIndex!].controls["PreventativeMaintenanceDocumentType"] = {
                        "value": "", valid: false
                    }
                }
            }

            return _state;
        }
        default:
            return state;
    }
}

const BLANK_FORM_VALUE = "__NO_FORM__";

function normalizeFormDocumentRevisionUSID(formValue: string): string {
    return formValue === BLANK_FORM_VALUE ? "" : formValue;
}

function resolveEnlilObjectValue(option: string): string {
    switch (option.toUpperCase()) {
        case "MANUFACTURING PROCESS INSTRUCTION":
            return "PI";
        case "CUSTOMER":
            return "CUSTOMERS";
        case "EQUIPMENT SPECIFICATION":
            return "EQUIPMENT_SPECIFICATION";
        case "APPROVAL REQUEST":
            return "APPROVAL_REQUEST";
        case "DOCUMENT METADATA (PATCH)":
            return "METADATA";
        default:
            return option.toUpperCase();
    }
}

export default function NewJob() {
    const navigate = useNavigate();
    const [etlJobEnterpriseComponent] = useState(makeEnterpriseComponent("ETLJob"));
    const [documentTypeEnterpriseComponent] = useState(makeEnterpriseComponent("DocumentType"));
    const [documentTypeAREnterpriseComponent] = useState(makeEnterpriseComponent("DocumentType"));
    const [calibrationDocumentTypeEnterpriseComponent] = useState(makeEnterpriseComponent("DocumentType"));
    const [preventativeMaintenanceDocumentTypeEnterpriseComponent] = useState(makeEnterpriseComponent("DocumentType"));
    const [documentTypeFormEnterpriseComponent] = useState(makeEnterpriseComponent("DocumentTypeForm"));
    const [documentTypeARFormEnterpriseComponent] = useState(makeEnterpriseComponent("DocumentTypeForm"));
    const [companySetupFormEnterpriseComponent] = useState(makeEnterpriseComponent("DocumentRevisionRD"));
    const [documentTypeFetchBackgroundOperation, setDocumentTypeFetchBackgroundOperation] = useState(false);
    const [documentTypeARFetchBackgroundOperation, setDocumentTypeARFetchBackgroundOperation] = useState(false);
    const [calibrationDocumentTypeFetchBackgroundOperation, setCalibrationDocumentTypeFetchBackgroundOperation] = useState(false);
    const [preventativeMaintenanceDocumentTypeFetchBackgroundOperation, setPreventativeMaintenanceDocumentTypeFetchBackgroundOperation] = useState(false);
    const [documentTypeFormFetchBackgroundOperation, setDocumentTypeFormFetchBackgroundOperation] = useState(false);
    const [documentTypeARFormFetchBackgroundOperation, setDocumentTypeARFormFetchBackgroundOperation] = useState(false);
    const [companySetupFormFetchBackgroundOperation, setCompanySetupFormFetchBackgroundOperation] = useState(false);
    const [saveBackgroundOperation, setSaveBackgroundOperation] = useState(false);
    const [documentTypeBuffer, setDocumentTypeBuffer] = React.useState<Map<string, any>[]>([]);
    const [documentTypeARBuffer, setDocumentTypeARBuffer] = React.useState<Map<string, any>[]>([]);
    const [calibrationDocumentTypeBuffer, setCalibrationDocumentTypeBuffer] = React.useState<Map<string, any>[]>([]);
    const [preventativeMaintenanceDocumentTypeBuffer, setPreventativeMaintenanceDocumentTypeBuffer] = React.useState<Map<string, any>[]>([]);
    const [formBuffer, setFormBuffer] = React.useState<Map<string, any>[]>([]);
    const [formBufferAR, setFormBufferAR] = React.useState<Map<string, any>[]>([]);
    const [companySetupFormBuffer, setCompanySetupFormBuffer] = React.useState<Map<string, any>[]>([]);
    const [showUploadSpreadsheet, setShowUploadSpreadsheet] = useState(false);
    const [jobUSID] = React.useState<string>(uuidv4());
    const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

    const [companyName, setCompanyName] = useState("");
    const [acronym, setAcronym] = useState("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [logoS3Key, setLogoS3Key] = useState("");
    const [logoUploadStatus, setLogoUploadStatus] = useState<'idle'|'uploading'|'done'|'error'>('idle');
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [avatarS3Key, setAvatarS3Key] = useState("");
    const [avatarUploadStatus, setAvatarUploadStatus] = useState<'idle'|'uploading'|'done'|'error'>('idle');
    const [isDuplicateForms, setIsDuplicateForms] = useState(false);
    const [allForms, setAllForms] = useState(true);
    const [selectedFormDocumentUSIDs, setSelectedFormDocumentUSIDs] = useState<string[]>([]);
    const [addAllEmployees, setAddAllEmployees] = useState(false);
    const [preserveOwner, setPreserveOwner] = useState(false);
    const [clonedCompanies, setClonedCompanies] = useState<Array<{USID: string, Name: string, CompanyAcronym?: string}>>([]);
    const [clonedCompaniesLoading, setClonedCompaniesLoading] = useState(false);
    const [selectedClonedCompanyUSID, setSelectedClonedCompanyUSID] = useState("");
    const [deltaForms, setDeltaForms] = useState<Array<{DocumentUSID: string, DocumentID: string, Label: string}>>([]);
    const [deltaFormsLoading, setDeltaFormsLoading] = useState(false);
    const [selectedDeltaFormDocumentUSIDs, setSelectedDeltaFormDocumentUSIDs] = useState<string[]>([]);
    const [purgeConfirmedName, setPurgeConfirmedName] = useState("");
    const [revisionCleanupMode, setRevisionCleanupMode] = useState("BOTH");
    const [revisionCleanupDocumentIds, setRevisionCleanupDocumentIds] = useState("");
    const [exportWithServerAutomation, setExportWithServerAutomation] = useState(false);
    const [exportLatestReleasedOnly, setExportLatestReleasedOnly] = useState(false);
    const [exportDocumentIds, setExportDocumentIds] = useState("");
    const [exportReleasedAtStart, setExportReleasedAtStart] = useState("");
    const [exportReleasedAtEnd, setExportReleasedAtEnd] = useState("");
    const [exportOnlyWithTasks, setExportOnlyWithTasks] = useState(false);
    const [exportOnlyWithMaterialDisposition, setExportOnlyWithMaterialDisposition] = useState(false);

    const excludeDocumentTypes = ['LHRT-', 'EFM-', 'MPI-','POAM-', 'DYF-','QPI-', 'AM-','CCC-'];

    const logoInputRef = useRef<HTMLInputElement>(null!);
    const avatarInputRef = useRef<HTMLInputElement>(null!);

    const [breadcrumb, setBreadcrumb] = useState<Map<string, string>>(new Map());

    const [transitions] = useState({
        "Type": {
            "Renderer": "renderType",
            "Import": "ImportSubType",
            "Export": "ExportSubType",
            "Execute": "ExecuteSubType",
            "Purge": "PurgeDocumentsUploadSpreadsheet"
        },
        "ImportSubType": {
            "Renderer": "renderImportSubType",
            "Import Enlil Object": "ImportEnlilObject",
            "Import Enlil Object Attachment": "UploadSpreadsheetFromLocalDisk",
            "Import Training Quiz": "UploadTrainingQuizSpreadsheet",
        },
        "UploadTrainingQuizSpreadsheet": {
            "Renderer": "renderUploadTrainingQuizSpreadsheet",
            "True": "CreateTrainingQuizJobButtons"
        },
        "CreateTrainingQuizJobButtons": {
            "Renderer": "renderCreateTrainingQuizJobButtons",
        },
        "ImportEnlilObject": {
            "Renderer": "renderImportEnlilObject",
            "CUSTOMERS": "DetermineImportObjectDocumentType",
            "BOM": "DetermineBOMUploadSource",
            "EQUIPMENT": "DetermineImportObjectDocumentType",
            "EQUIPMENT_SPECIFICATION": "DetermineImportObjectDocumentType",
            "LOT": "DetermineImportObjectDocumentType",
            "PART": "DetermineImportObjectDocumentType",
            "DOCUMENT": "DetermineImportObjectDocumentType",
            "PI": "DeterminePIImportObjectDocumentType",
            "RECORD": "DetermineImportObjectDocumentType",
            "METADATA": "DetermineUploadSource",
            "APPROVAL_REQUEST": "DetermineARImportObjectDocumentType",
            "SUPPLIER": "DetermineImportObjectDocumentType",
        },
        "DetermineBOMUploadSource": {
            "Renderer": "renderDetermineBOMUploadSource",
            "LOCAL_DISK": "UploadSpreadsheetFromLocalDisk",
            "GOOGLE_DRIVE": "UploadSpreadsheetFromLocalDisk",
            "DROPBOX": "UploadSpreadsheetFromLocalDisk",
            "S3": "UploadSpreadsheetFromLocalDisk"
        },
        "DeterminePIImportObjectDocumentType": {
            "Renderer": "renderDeterminePIImportObjectDocumentType",
            "ANY": "DetermineARImportObjectDocumentType"
        },
        "DetermineImportObjectDocumentType": {
            "Renderer": "renderDetermineImportObjectDocumentType",
            "ANY": "DetermineImportDocumentTypeForm"
        },
        "DetermineImportDocumentTypeForm": {
            "Renderer": "renderDetermineImportDocumentTypeForm",
            "ANY": "DetermineUploadSource"
        },
        "DetermineARImportObjectDocumentType": {
            "Renderer": "renderDetermineARImportObjectDocumentType",
            "ANY": "DetermineARImportDocumentTypeForm"
        },
        "DetermineARImportDocumentTypeForm": {
            "Renderer": "renderDetermineARImportDocumentTypeForm",
            "ANY": "DetermineUploadSource"
        },
        "ExportARImportDocumentTypeForm": {
            "Renderer": "renderExportARImportDocumentTypeForm_CreateJobButtons",
        },
        "DetermineUploadSource": {
            "Renderer": "renderSelectUploadSource",
            "LOCAL_DISK": "UploadSpreadsheetFromLocalDisk"
        },
        "UploadSpreadsheetFromLocalDisk": {
            "Renderer": "renderUploadSpreadsheetFromLocalDisk",
            "True": "CreateJobButtons"
        },
        "PurgeDocumentsUploadSpreadsheet": {
            "Renderer": "renderUploadSpreadsheetFromLocalDisk",
            "True": "ExecutePurgeCreateJobButtons"
        },
        "CreateJobButtons": {
            "Renderer": "renderImport_ImportEnlilObject_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons",
        },
        "ExecutePurgeCreateJobButtons": {
            "Renderer": "renderExecutePurgeCreateJobButtons",
        },

        "ExportSubType": {
            "Renderer": "renderExportSubType",
            "Export Enlil Object(s)": "ExportEnlilObject",
            "Export Enlil Template": "Export_ExportEnlilTemplate",
            "Export Enlil Data": "Export_ExportEnlilData",
            "Export Training Quiz Template": "ExportTrainingQuizTemplate_CreateJobButtons"
        },
        "ExportTrainingQuizTemplate_CreateJobButtons": {
            "Renderer": "renderExportTrainingQuizTemplate_CreateJobButtons",
        },
        "Export_ExportEnlilData": {
            "Renderer": "renderExport_ExportEnlilData",
            "PART": "Export_ExportEnlilData_CreateJobButtons",
            "DOCUMENT": "Export_ExportEnlilData_CreateJobButtons",
            "RECORD": "Export_ExportEnlilData_CreateJobButtons",
            "SUPPLIER": "Export_ExportEnlilData_CreateJobButtons",
            "CUSTOMERS": "Export_ExportEnlilData_CreateJobButtons",
            "EQUIPMENT": "Export_ExportEnlilData_CreateJobButtons",
            "EQUIPMENT_SPECIFICATION": "Export_ExportEnlilData_CreateJobButtons",
            "LOT": "Export_ExportEnlilData_CreateJobButtons",
            "PI": "Export_ExportEnlilData_CreateJobButtons",
            "APPROVAL_REQUEST": "Export_ExportEnlilData_CreateJobButtons",
            "BOM": "Export_ExportEnlilData_CreateJobButtons",
            "ATTACHMENT": "Export_ExportEnlilData_CreateJobButtons"
        },
        "Export_ExportEnlilData_CreateJobButtons": {
            "Renderer": "renderExport_ExportEnlilData_CreateJobButtons",
        },
        "ExecuteSubType": {
            "Renderer": "renderExecuteSubType",
            "Enlil Service": "ExecuteEnlilService"
        },
        "ExecuteEnlilService": {
            "Renderer": "renderExecuteEnlilService",
            "Company Duplication": "ExecuteCompanyDuplication",
            "Company Setup SQL": "ExecuteCompanySetupSql",
            "Copy Delta Forms": "ExecuteCopyDeltaForms",
            "Company Purge": "ExecuteCompanyPurge",
            "Revision Cleanup": "ExecuteRevisionCleanup",
        },
        "ExecuteCompanyDuplication": {
            "Renderer": "renderExecuteCompanyDuplication",
        },
        "ExecuteCompanySetupSql": {
            "Renderer": "renderExecuteCompanySetupSql",
        },
        "ExecuteCopyDeltaForms": {
            "Renderer": "renderExecuteCopyDeltaForms",
        },
        "ExecuteCompanyPurge": {
            "Renderer": "renderExecuteCompanyPurge",
        },
        "ExecuteRevisionCleanup": {
            "Renderer": "renderExecuteRevisionCleanup",
        },
        "ExportEnlilObject": {
            "Renderer": "renderExportEnlilObject",
            "EQUIPMENT": "SelectEquipmentDownloadVolume"
        },
        "Export_ExportEnlilTemplate": {
            "Renderer": "renderExport_ExportEnlilTemplate",
            "ATTACHMENT": "Export_ExportEnlilTemplate_Object_AttachmentTemplateDownload",
            "BOM": "Export_ExportEnlilTemplate_Object_BOMTemplateDownload",
            "CUSTOMERS": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
            "DOCUMENT": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
            "EQUIPMENT": "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType",
            "EQUIPMENT_SPECIFICATION": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
            "LOT": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
            "PART": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
            "PI": "Export_ExportEnlilTemplate_Object_PIDetermineObjectDocumentType",
            "RECORD": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
            "METADATA": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons",
            "APPROVAL_REQUEST": "DetermineARImportObjectDocumentType",
            "SUPPLIER": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
        },
        "Export_ExportEnlilTemplate_Object_BOMTemplateDownload": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_BOMTemplateDownload",
        },
        "Export_ExportEnlilTemplate_Object_AttachmentTemplateDownload": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_AttachmentTemplateDownload",
        },

        "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType",
            "ANY": "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm"
        },
        "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm",
            "ANY": "Export_ExportEnlilTemplate_Object_EquipmentCalibration_DetermineObjectDocumentType"
        },
        "Export_ExportEnlilTemplate_Object_EquipmentCalibration_DetermineObjectDocumentType": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_EquipmentCalibration_DetermineObjectDocumentType",
            "ANY": "Export_ExportEnlilTemplate_Object_EquipmentPreventativeMaintenance_DetermineObjectDocumentType"
        },
        "Export_ExportEnlilTemplate_Object_EquipmentPreventativeMaintenance_DetermineObjectDocumentType": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_EquipmentPreventativeMaintenance_DetermineObjectDocumentType",
            "ANY": "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons"
        },
        "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons",
        },
        "Export_ExportEnlilTemplate_Object_PIDetermineObjectDocumentType": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_PIDetermineObjectDocumentType",
            "ANY": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons"
        },


        "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType",
            "ANY": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm"
        },
        "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm",
            "ANY": "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons"
        },
        "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons": {
            "Renderer": "renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons",
        },

        "SelectEquipmentDownloadVolume": {
            "Renderer": "renderSelectEquipmentDownloadVolume",
            "Equipment Template": "renderEquipmentTemplate",
            "1+ Equipment Volume": "render1PlusEquipmentVolume",
            "All Equipment Volume": "renderAllEquipmentVolume",
        },
    });

    const [state, dispatch] = useReducer(stepReducer, {
        steps: [{
            controls: {
                "Name": {"value": ""},
                "Type": {"value": ""},
                "SubType": {"value": ""},
                "Object": {"value": ""},
                "DocumentType": {"value": ""},
                "DocumentTypeGroup": {"value": ""},
                "ARDocumentType": {"value": ""},
                "Form": {"value": ""},
                "ARForm": {"value": ""},
                "CalibrationDocumentType": {"value": ""},
                "PreventativeMaintenanceDocumentType": {"value": ""},
                "ExportType": {"value": ""},
                "UploadSource": {"value": ""},
                "DownloadVolume": {"value": ""},
            }
        }]
    });

    useEffect(() => {
        EventBus.components.addListener("upload-spreadsheet-cancel", onCancelHandler);
        EventBus.components.addListener("upload-spreadsheet-done", onUploadSpreadSheetSelection);

        documentTypeEnterpriseComponent.addLoadStartedListener(enterpriseComponentLoadStartedListener);
        documentTypeEnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);
        documentTypeFormEnterpriseComponent.addLoadStartedListener(enterpriseComponentLoadStartedListener);
        documentTypeFormEnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);
        companySetupFormEnterpriseComponent.addLoadStartedListener(enterpriseComponentLoadStartedListener);
        companySetupFormEnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);

        documentTypeAREnterpriseComponent.addLoadStartedListener(enterpriseComponentLoadStartedListener);
        documentTypeAREnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);
        documentTypeARFormEnterpriseComponent.addLoadStartedListener(enterpriseComponentLoadStartedListener);
        documentTypeARFormEnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);

        calibrationDocumentTypeEnterpriseComponent.addLoadStartedListener(enterpriseComponentLoadStartedListener);
        calibrationDocumentTypeEnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);
        preventativeMaintenanceDocumentTypeEnterpriseComponent.addLoadStartedListener(enterpriseComponentLoadStartedListener);
        preventativeMaintenanceDocumentTypeEnterpriseComponent.addLoadCompletedListener(enterpriseComponentLoadCompletedListener);

        return () => {
            EventBus.components.removeListener("upload-spreadsheet-cancel", onCancelHandler);
            EventBus.components.removeListener("upload-spreadsheet-done", onUploadSpreadSheetSelection);

            documentTypeEnterpriseComponent.removeLoadStartedListener(enterpriseComponentLoadStartedListener)
            documentTypeEnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener)
            documentTypeFormEnterpriseComponent.removeLoadStartedListener(enterpriseComponentLoadStartedListener)
            documentTypeFormEnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener)
            companySetupFormEnterpriseComponent.removeLoadStartedListener(enterpriseComponentLoadStartedListener)
            companySetupFormEnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener)

            documentTypeAREnterpriseComponent.removeLoadStartedListener(enterpriseComponentLoadStartedListener)
            documentTypeAREnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener)
            documentTypeARFormEnterpriseComponent.removeLoadStartedListener(enterpriseComponentLoadStartedListener)
            documentTypeARFormEnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener)

            calibrationDocumentTypeEnterpriseComponent.removeLoadStartedListener(enterpriseComponentLoadStartedListener)
            calibrationDocumentTypeEnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener)
            preventativeMaintenanceDocumentTypeEnterpriseComponent.removeLoadStartedListener(enterpriseComponentLoadStartedListener)
            preventativeMaintenanceDocumentTypeEnterpriseComponent.removeLoadCompletedListener(enterpriseComponentLoadCompletedListener)
        }
    }, [documentTypeEnterpriseComponent, documentTypeAREnterpriseComponent]);

    function changeHandler(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, formElementId: string, directive: string) {
        const updatedFormElement = {
            value: event.target.value,
            touched: true,
            valid: true,
            helperText: null,
        };

        let b = breadcrumb
        b.set(directive, event.target.value)
        setBreadcrumb(b)

        dispatch({type: "CONTROL_UPDATE", stepIndex: 0, control: updatedFormElement, formElementId: formElementId, step: undefined});

        if (formElementId === "DocumentType") {
            const selectedType = documentTypeBuffer.find(
                (element: any) => element.get("USID") === event.target.value,
            );
            const documentTypeGroup = selectedType?.get("Group") ?? "";
            dispatch({
                type: "CONTROL_UPDATE",
                stepIndex: 0,
                control: {value: documentTypeGroup, touched: true, valid: true, helperText: null},
                formElementId: "DocumentTypeGroup",
                step: undefined,
            });

            if (
                documentTypeGroup === "OTHER"
                && breadcrumb.get("ImportEnlilObject") === "DOCUMENT"
            ) {
                documentTypeAREnterpriseComponent.newQuery();
                documentTypeAREnterpriseComponent.setAttributeValue("Group", "CHANGE_REQUEST");
                documentTypeAREnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
                documentTypeAREnterpriseComponent.executeQuery(1, 1000);
            }
        }

        if (formElementId === "Object" && directive !== "ExecuteEnlilService" && directive !== "Export_ExportEnlilData" && event.target.value !== "BOM" && event.target.value !== "ATTACHMENT" && event.target.value !== "APPROVAL_REQUEST" && event.target.value !== "METADATA") {
            documentTypeEnterpriseComponent.newQuery();
            if (event.target.value === "DOCUMENT") {
                // @ts-ignore
                documentTypeEnterpriseComponent.isDocumentQuery = true;
            } else {
                documentTypeEnterpriseComponent.setAttributeValue("Group", event.target.value);
            }
            documentTypeEnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
            documentTypeEnterpriseComponent.executeQuery(1, 1000);
        }
        if (formElementId === "Object" && event.target.value === "APPROVAL_REQUEST") {
            documentTypeAREnterpriseComponent.newQuery();
            documentTypeAREnterpriseComponent.setAttributeValue("Group", "CHANGE_REQUEST");
            documentTypeAREnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
            documentTypeAREnterpriseComponent.executeQuery(1, 1000);
        }
        if (formElementId === "DocumentType" && breadcrumb.get("ImportEnlilObject") === "PI") {
            documentTypeAREnterpriseComponent.newQuery();
            documentTypeAREnterpriseComponent.setAttributeValue("Group", "CHANGE_REQUEST");
            documentTypeAREnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
            documentTypeAREnterpriseComponent.executeQuery(1, 1000);
        }
        if (formElementId === "Form" && breadcrumb.get("ImportEnlilObject") === "PART") {
            documentTypeAREnterpriseComponent.newQuery();
            documentTypeAREnterpriseComponent.setAttributeValue("Group", "CHANGE_REQUEST");
            documentTypeAREnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
            documentTypeAREnterpriseComponent.executeQuery(1, 1000);
        }
        if (formElementId === "Form" && breadcrumb.get("ImportEnlilObject") === "EQUIPMENT_SPECIFICATION") {
            documentTypeAREnterpriseComponent.newQuery();
            documentTypeAREnterpriseComponent.setAttributeValue("Group", "CHANGE_REQUEST");
            documentTypeAREnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
            documentTypeAREnterpriseComponent.executeQuery(1, 1000);
        }
        if (formElementId === "Form" && breadcrumb.get("ImportEnlilObject") === "DOCUMENT") {
            documentTypeAREnterpriseComponent.newQuery();
            documentTypeAREnterpriseComponent.setAttributeValue("Group", "CHANGE_REQUEST");
            documentTypeAREnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
            documentTypeAREnterpriseComponent.executeQuery(1, 1000);
        }
    }

    function cancelHandler() {
        navigate("/jobs");
    }

    function isAttachmentImportJob(): boolean {
        return state.steps[0].controls["SubType"]?.value === "Import Enlil Object Attachment";
    }

    function isTrainingQuizImportJob(): boolean {
        return state.steps[0].controls["SubType"]?.value === "Import Training Quiz";
    }

    function findAttachmentSpreadsheetFile(files: any[]) {
        return files.find((file: any) => /\.(xlsx|xlsm)$/i.test(file.name));
    }

    function findAttachmentZipFile(files: any[]) {
        return files.find((file: any) => /\.zip$/i.test(file.name));
    }

    function attachmentImportFilesReady(files: any[]): boolean {
        return findAttachmentSpreadsheetFile(files) != null && findAttachmentZipFile(files) != null;
    }

    function mergeAttachmentUploadSelection(previousFiles: any[], newFiles: any[]) {
        const merged = [...previousFiles];
        for (const file of newFiles) {
            if (!merged.some((existing: any) => existing.name === file.name)) {
                merged.push(file);
            }
        }
        return merged;
    }

    function onUploadSpreadSheetSelection(args: any) {
        if (isAttachmentImportJob()) {
            setSelectedFiles((previousFiles: any[]) => mergeAttachmentUploadSelection(previousFiles, args));
        } else {
            setSelectedFiles(args);
        }
        setShowUploadSpreadsheet(false);
    }

    function enterpriseComponentLoadStartedListener(ec: EnterpriseComponent) {
        if (ec.serialNumber === documentTypeEnterpriseComponent.serialNumber) {
            setDocumentTypeFetchBackgroundOperation(true)
        }
        if (ec.serialNumber === documentTypeFormEnterpriseComponent.serialNumber) {
            setDocumentTypeFormFetchBackgroundOperation(true)
        }
        if (ec.serialNumber === companySetupFormEnterpriseComponent.serialNumber) {
            setCompanySetupFormFetchBackgroundOperation(true)
        }
        if (ec.serialNumber === documentTypeAREnterpriseComponent.serialNumber) {
            setDocumentTypeARFetchBackgroundOperation(true)
        }
        if (ec.serialNumber === documentTypeARFormEnterpriseComponent.serialNumber) {
            setDocumentTypeARFormFetchBackgroundOperation(true)
        }
        if (ec.serialNumber === calibrationDocumentTypeEnterpriseComponent.serialNumber) {
            setCalibrationDocumentTypeFetchBackgroundOperation(true)
        }
        if (ec.serialNumber === preventativeMaintenanceDocumentTypeEnterpriseComponent.serialNumber) {
            setPreventativeMaintenanceDocumentTypeFetchBackgroundOperation(true)
        }
    }

    function enterpriseComponentLoadCompletedListener(ec: EnterpriseComponent) {
        let b:Map<string, any>[] = prepareBuffer(ec)

        if (ec.serialNumber === documentTypeEnterpriseComponent.serialNumber) {
            setDocumentTypeBuffer(b);
            setDocumentTypeFetchBackgroundOperation(false)
        }
        if (ec.serialNumber === documentTypeFormEnterpriseComponent.serialNumber) {
            setFormBuffer(b);
            setDocumentTypeFormFetchBackgroundOperation(false)
        }
        if (ec.serialNumber === companySetupFormEnterpriseComponent.serialNumber) {
            setCompanySetupFormBuffer(b);
            setCompanySetupFormFetchBackgroundOperation(false)
        }
        if (ec.serialNumber === documentTypeAREnterpriseComponent.serialNumber) {
            setDocumentTypeARBuffer(b);
            setDocumentTypeARFetchBackgroundOperation(false)
        }
        if (ec.serialNumber === documentTypeARFormEnterpriseComponent.serialNumber) {
            setFormBufferAR(b);
            setDocumentTypeARFormFetchBackgroundOperation(false)
        }
        if (ec.serialNumber === calibrationDocumentTypeEnterpriseComponent.serialNumber) {
            setCalibrationDocumentTypeBuffer(b);
            setCalibrationDocumentTypeFetchBackgroundOperation(false)
        }
        if (ec.serialNumber === preventativeMaintenanceDocumentTypeEnterpriseComponent.serialNumber) {
            setPreventativeMaintenanceDocumentTypeBuffer(b);
            setPreventativeMaintenanceDocumentTypeFetchBackgroundOperation(false)
        }
    }

    function onCancelHandler() {
        setShowUploadSpreadsheet(false);
    }

    function downloadStaticTemplateFile(url: string, filename: string) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    function renderExportTemplateDownloadCard(onDownload: () => void) {
        return (
            <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 20px 20px", padding: "12px", border: "1px solid #E0E0E0", borderRadius: "5px", backgroundColor: "#F5F7FA"}}>
                <Typography style={{fontSize: "13px", fontWeight: 400, color: "#5A6474", marginBottom: "12px"}}>
                    No job will be created.
                </Typography>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    onClick={onDownload}
                    style={{alignSelf: "flex-start"}}
                >
                    Download Template
                </CCButton>
            </div>
        );
    }

    function renderExportAttachmentTemplateDownloadCard(
        templates: { url: string; filename: string; label: string }[]
    ) {
        return (
            <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 20px 20px", padding: "12px", border: "1px solid #E0E0E0", borderRadius: "5px", backgroundColor: "#F5F7FA"}}>
                <Typography style={{fontSize: "13px", fontWeight: 400, color: "#5A6474", marginBottom: "12px"}}>
                    No job will be created.
                </Typography>
                <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
                    {templates.map((template) => (
                        <CCButton
                            key={template.filename}
                            className={css_buttons.blueStandardButton}
                            onClick={() => downloadStaticTemplateFile(template.url, template.filename)}
                            style={{alignSelf: "flex-start"}}
                        >
                            {template.label}
                        </CCButton>
                    ))}
                </div>
            </div>
        );
    }

    // Export/Export Enlil Template
    function renderExportSubType() {
        let subTypeOptions: Array<{label: string, value: string}> = [
            {label: "Export Enlil Template", value: "Export Enlil Template"},
            // {label: "Export Training Quiz Template", value: "Export Training Quiz Template"},
        ]
        if (hasExportEnlilDataPermission()) {
            subTypeOptions.push({label: "Data", value: "Export Enlil Data"})
        }

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="SubType"
                        margin="dense"
                        fullWidth={true}
                        id="SubType"
                        label="Sub Type *"
                        onChange={(event) => changeHandler(event, "SubType", "ExportSubType")}
                        value={state.steps[0].controls["SubType"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {subTypeOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("ExportSubType", state.steps[0].controls["SubType"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilData() {
        const entityOptions = [
            {label: "Customer", value: "CUSTOMERS", enabled: true},
            {label: "Document", value: "DOCUMENT", enabled: true},
            {label: "Equipment", value: "EQUIPMENT", enabled: true},
            {label: "Equipment Specification", value: "EQUIPMENT_SPECIFICATION", enabled: true},
            {label: "Lot", value: "LOT", enabled: true},
            {label: "Manufacturing Process Instruction", value: "PI", enabled: true},
            {label: "Part", value: "PART", enabled: true},
            {label: "Record", value: "RECORD", enabled: true},
            {label: "Supplier", value: "SUPPLIER", enabled: true},
            {label: "Approval Request", value: "APPROVAL_REQUEST", enabled: true},
            {label: "Attachment", value: "ATTACHMENT", enabled: true},
            {label: "BOM", value: "BOM", enabled: true},
        ]

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="Object"
                        margin="dense"
                        fullWidth={true}
                        id="Object"
                        label="Entity *"
                        onChange={(event) => changeHandler(event, "Object", "Export_ExportEnlilData")}
                        value={state.steps[0].controls["Object"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {entityOptions.map(option => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                disabled={!option.enabled}>
                                {option.enabled ? option.label : `${option.label} (Not available yet)`}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div style={{display: "flex", flexDirection: "row", alignItems: "center", margin: "0px 20px 20px 20px"}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={exportWithServerAutomation}
                                onChange={(e) => setExportWithServerAutomation(e.target.checked)}
                                id="ExportWithServerAutomation"
                            />
                        }
                        label="Export with Server Automation (replace employee references)"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={exportLatestReleasedOnly}
                                onChange={(e) => setExportLatestReleasedOnly(e.target.checked)}
                                id="ExportLatestReleasedOnly"
                            />
                        }
                        label="Export latest released revision only"
                    />
                </div>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        margin="dense"
                        fullWidth={true}
                        id="ExportDocumentIds"
                        label={
                            state.steps[0].controls["Object"].value === "APPROVAL_REQUEST"
                                ? "AR #s / Document IDs (optional, comma-separated; wildcards * ? % supported)"
                                : "Document IDs (optional, comma-separated; wildcards * ? % supported)"
                        }
                        placeholder={
                            state.steps[0].controls["Object"].value === "APPROVAL_REQUEST"
                                ? "DCO*, ECO-001"
                                : "CS-100*, PART-001"
                        }
                        helperText={
                            state.steps[0].controls["Object"].value === "APPROVAL_REQUEST"
                                ? "For Approval Request, matches AR # (change_request.crId)"
                                : undefined
                        }
                        value={exportDocumentIds}
                        onChange={(e) => setExportDocumentIds(e.target.value)}
                        variant="outlined"
                        slotProps={{
                            inputLabel: {shrink: true}
                        }}
                    />
                </div>
                <div style={{display: "flex", flexDirection: "row", gap: "20px", margin: "0px 20px 20px 20px"}}>
                    <CCDatePicker
                        label="Released At From"
                        value={exportReleasedAtStart ? moment(exportReleasedAtStart) : undefined}
                        onChange={(date: any) => setExportReleasedAtStart(date || "")}
                        slotProps={{
                            textField: {
                                margin: "dense",
                                fullWidth: true,
                                helperText: "For Approval Request, filters Closed Datetime",
                            },
                        }}
                    />
                    <CCDatePicker
                        label="Released At To"
                        value={exportReleasedAtEnd ? moment(exportReleasedAtEnd) : undefined}
                        minDate={exportReleasedAtStart ? moment(exportReleasedAtStart) : undefined}
                        onChange={(date: any) => setExportReleasedAtEnd(date || "")}
                        slotProps={{
                            textField: {
                                margin: "dense",
                                fullWidth: true,
                                helperText: "If From is set and To is empty, defaults to today",
                            },
                        }}
                    />
                </div>
                {state.steps[0].controls["Object"].value === "APPROVAL_REQUEST" && (
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", margin: "0px 20px 20px 20px"}}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={exportOnlyWithTasks}
                                    onChange={(e) => setExportOnlyWithTasks(e.target.checked)}
                                    id="ExportOnlyWithTasks"
                                />
                            }
                            label="Only ARs with Tasks"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={exportOnlyWithMaterialDisposition}
                                    onChange={(e) => setExportOnlyWithMaterialDisposition(e.target.checked)}
                                    id="ExportOnlyWithMaterialDisposition"
                                />
                            }
                            label="Only ARs with Material Disposition"
                        />
                    </div>
                )}
                {renderTransition("Export_ExportEnlilData", state.steps[0].controls["Object"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilData_CreateJobButtons() {
        if (!hasExportEnlilDataPermission()) {
            return null
        }

        return (
            <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                <CCButton
                    className={css_buttons.button}
                    style={{marginRight: "10px"}}
                    disabled={saveBackgroundOperation}
                    onClick={() => saveExportEnlilDataHandler("Save")}>
                    Save
                </CCButton>
                <CCButton
                    className={css_buttons.button}
                    disabled={saveBackgroundOperation}
                    onClick={() => saveExportEnlilDataHandler("Execute")}>
                    Save & Execute
                </CCButton>
            </div>
        )
    }

    // Export/Export Enlil Template/Equipment Document Type/Equipment Document Form/CreateJobButtons

    function renderExport_ExportEnlilTemplate() {
        let subTypeOptions = [
            "Attachment",
            "BOM",
            "Customer",
            "Document",
            "Equipment",
            "Equipment Specification",
            "Lot",
            "Manufacturing Process Instruction",
            "Part",
            "Record",
            "Supplier",
            "Approval Request",
        ]

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="Object"
                        margin="dense"
                        fullWidth={true}
                        id="Object"
                        label="Object *"
                        onChange={(event) => changeHandler(event, "Object", "Export_ExportEnlilTemplate")}
                        value={state.steps[0].controls["Object"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {subTypeOptions.map(option => (
                            <MenuItem key={option} value={resolveEnlilObjectValue(option)}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("Export_ExportEnlilTemplate", state.steps[0].controls["Object"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_BOMTemplateDownload() {
        return renderExportTemplateDownloadCard(() => {
            downloadStaticTemplateFile(
                'https://enlil-sample-public.s3.us-west-2.amazonaws.com/bom-sample/Bom.xlsx',
                'Bom.xlsx'
            );
        });
    }

    function renderExport_ExportEnlilTemplate_Object_AttachmentTemplateDownload() {
        return renderExportAttachmentTemplateDownloadCard([
            {
                url: 'https://enlil-sample-public.s3.us-west-2.amazonaws.com/attachment-sample/attachment.xlsx',
                filename: 'attachment.xlsx',
                label: 'Download attachment.xlsx',
            },
            {
                url: 'https://enlil-sample-public.s3.us-west-2.amazonaws.com/attachment-sample/Attachments.zip',
                filename: 'Attachments.zip',
                label: 'Download Attachments.zip',
            },
        ]);
    }

    function renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="DocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="DocumentType"
                            label="Object Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "DocumentType", "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType");

                                documentTypeFormEnterpriseComponent.newQuery();
                                documentTypeFormEnterpriseComponent.setAttributeValue("USID", event.target.value);
                                documentTypeFormEnterpriseComponent.setIDOCResponse("v1.DocumentType-002");
                                documentTypeFormEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["DocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {documentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType", state.steps[0].controls["DocumentType"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFormFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="Form"
                            margin="dense"
                            fullWidth={true}
                            id="Form"
                            label="Form *"
                            onChange={(event) => {
                                changeHandler(event, "Form", "Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm")

                                calibrationDocumentTypeEnterpriseComponent.newQuery();
                                calibrationDocumentTypeEnterpriseComponent.setAttributeValue("Group", "MAINTENANCE_RECORD");
                                calibrationDocumentTypeEnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
                                calibrationDocumentTypeEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["Form"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {getFormTemplates(formBuffer).map((template: any) => (
                                <MenuItem key={template.DocumentRevisionUSID} value={template.DocumentRevisionUSID}>
                                    {`${template.DocumentRevision.DocumentID} Rev ${template.DocumentRevision.DisplayRevision}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("Export_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm", state.steps[0].controls["Form"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_EquipmentCalibration_DetermineObjectDocumentType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {calibrationDocumentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="CalibrationDocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="CalibrationDocumentType"
                            label="Calibration Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "CalibrationDocumentType", "Export_ExportEnlilTemplate_Object_EquipmentCalibration_DetermineObjectDocumentType");

                                preventativeMaintenanceDocumentTypeEnterpriseComponent.newQuery();
                                preventativeMaintenanceDocumentTypeEnterpriseComponent.setAttributeValue("Group", "MAINTENANCE_RECORD");
                                preventativeMaintenanceDocumentTypeEnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
                                preventativeMaintenanceDocumentTypeEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["CalibrationDocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {calibrationDocumentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("Export_ExportEnlilTemplate_Object_EquipmentCalibration_DetermineObjectDocumentType", state.steps[0].controls["CalibrationDocumentType"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_EquipmentPreventativeMaintenance_DetermineObjectDocumentType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {preventativeMaintenanceDocumentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="PreventativeMaintenanceDocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="PreventativeMaintenanceDocumentType"
                            label="Preventative Maintenance Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "PreventativeMaintenanceDocumentType", "Export_ExportEnlilTemplate_Object_EquipmentPreventativeMaintenance_DetermineObjectDocumentType");
                            }}
                            value={state.steps[0].controls["PreventativeMaintenanceDocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {preventativeMaintenanceDocumentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("Export_ExportEnlilTemplate_Object_EquipmentPreventativeMaintenance_DetermineObjectDocumentType", state.steps[0].controls["PreventativeMaintenanceDocumentType"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons() {
        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                <div style={{marginRight: "20px"}}>
                    <CCButton
                        className={css_buttons.redStandardButton}
                        onClick={() => cancelHandler()}>
                        Cancel
                    </CCButton>
                </div>
                <div style={{marginRight: "10px"}}>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveExportEquipmentTemplateHandler("Save")}>
                        Save
                    </CCButton>
                </div>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    animate={saveBackgroundOperation}
                        onClick={() => saveExportEquipmentTemplateHandler("Execute")}>
                    Save & Execute
                </CCButton>
            </div>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_PIDetermineObjectDocumentType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="DocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="DocumentType"
                            label="Object Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "DocumentType", "Export_ExportEnlilTemplate_Object_PIDetermineObjectDocumentType");

                                documentTypeFormEnterpriseComponent.newQuery();
                                documentTypeFormEnterpriseComponent.setAttributeValue("USID", event.target.value);
                                documentTypeFormEnterpriseComponent.setIDOCResponse("v1.DocumentType-002");
                                documentTypeFormEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["DocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {documentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("Export_ExportEnlilTemplate_Object_PIDetermineObjectDocumentType", state.steps[0].controls["DocumentType"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="DocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="DocumentType"
                            label="Object Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "DocumentType", "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType");

                                documentTypeFormEnterpriseComponent.newQuery();
                                documentTypeFormEnterpriseComponent.setAttributeValue("USID", event.target.value);
                                documentTypeFormEnterpriseComponent.setIDOCResponse("v1.DocumentType-002");
                                documentTypeFormEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["DocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {documentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType", state.steps[0].controls["DocumentType"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFormFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="Form"
                            margin="dense"
                            fullWidth={true}
                            id="Form"
                            label={allowsBlankFormForSelectedDocumentType() ? "Form" : "Form *"}
                            onChange={(event) => {
                                changeHandler(event, "Form", "Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm")
                            }}
                            value={state.steps[0].controls["Form"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {renderFormSelectMenuItems(formBuffer)}
                        </TextField>
                    )}
                </div>
                {renderTransition("Export_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm", state.steps[0].controls["Form"].value)}
            </>
        )
    }

    function renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons() {
        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                <div style={{marginRight: "20px"}}>
                    <CCButton
                        className={css_buttons.redStandardButton}
                        onClick={() => cancelHandler()}>
                        Cancel
                    </CCButton>
                </div>
                <div style={{marginRight: "10px"}}>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveExportTemplateHandler("Save")}>
                        Save
                    </CCButton>
                </div>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    animate={saveBackgroundOperation}
                        onClick={() => saveExportTemplateHandler("Execute")}>
                    Save & Execute
                </CCButton>
            </div>
        )
    }

    function renderExportEnlilObject() {
        let subTypeOptions = [
            "Customer",
            "Document",
            "Equipment",
            "Equipment Specification",
            "Lot",
            "Manufacturing Process Instruction",
            "Part",
            "Record",
            "Supplier",
            "Approval Request",
        ]

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="Object"
                        margin="dense"
                        fullWidth={true}
                        id="Object"
                        label="Object *"
                        onChange={(event) => changeHandler(event, "Object", "ExportEnlilObject")}
                        value={state.steps[0].controls["Object"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {subTypeOptions.map(option => (
                            <MenuItem key={option} value={option.toUpperCase() === 'APPROVAL REQUEST' ? 'APPROVAL_REQUEST' : option.toUpperCase()}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("ExportEnlilObject", state.steps[0].controls["Object"].value)}
            </>
        )
    }



    function renderImportSubType() {
        let subTypeOptions = [
            "Import Enlil Object",
            "Import Enlil Object Attachment",
            // "Import Training Quiz",
        ]

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="SubType"
                        margin="dense"
                        fullWidth={true}
                        id="SubType"
                        label="Sub Type *"
                        onChange={(event) => changeHandler(event, "SubType", "ImportSubType")}
                        value={state.steps[0].controls["SubType"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {subTypeOptions.map(option => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("ImportSubType", state.steps[0].controls["SubType"].value)}
            </>
        )
    }

    function renderImportEnlilObject() {
        let subTypeOptions = [
            "BOM",
            "Customer",
            "Document",
            "Equipment",
            "Equipment Specification",
            "Lot",
            "Manufacturing Process Instruction",
            "Part",
            "Record",
            "Document metadata (patch)",
            "Supplier",
            "Approval Request",
        ]

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="Object"
                        margin="dense"
                        fullWidth={true}
                        id="Object"
                        label="Object *"
                        onChange={(event) => changeHandler(event, "Object", "ImportEnlilObject")}
                        value={state.steps[0].controls["Object"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {subTypeOptions.map(option => (
                            <MenuItem key={option} value={resolveEnlilObjectValue(option)}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("ImportEnlilObject", state.steps[0].controls["Object"].value)}
            </>
        )
    }

    function renderDetermineBOMUploadSource() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="UploadSource"
                        margin="dense"
                        fullWidth={true}
                        id="Form"
                        label="Upload Source *"
                        onChange={(event) => {
                            changeHandler(event, "UploadSource", "DetermineBOMUploadSource")
                        }}
                        value={state.steps[0].controls["UploadSource"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        <MenuItem key={"Local Disk"} value={"LOCAL_DISK"}>
                            Local Disk
                        </MenuItem>
                        <MenuItem key={"GoogleDrive"} value={"GOOGLE_DRIVE"}>
                            Google Drive
                        </MenuItem>
                        <MenuItem key={"DropBox"} value={"DROPBOX"}>
                            Dropbox
                        </MenuItem>
                        <MenuItem key={"S3"} value={"S3"}>
                            S3
                        </MenuItem>
                    </TextField>
                </div>
                {renderTransition("DetermineBOMUploadSource", state.steps[0].controls["UploadSource"].value)}
            </>
        )
    }

    function renderDetermineImportObjectDocumentType() {
        const documentExcludeAcronyms = breadcrumb.get("ImportEnlilObject") === "DOCUMENT" ? excludeDocumentTypes : [];

        const filteredDocumentTypeBuffer = documentTypeBuffer.filter((element: any) =>
            !documentExcludeAcronyms.includes(element.get("DocumentTypeAcronym"))
        );

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="DocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="DocumentType"
                            label="Object Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "DocumentType", "DetermineImportObjectDocumentType");

                                documentTypeFormEnterpriseComponent.newQuery();
                                documentTypeFormEnterpriseComponent.setAttributeValue("USID", event.target.value);
                                documentTypeFormEnterpriseComponent.setIDOCResponse("v1.DocumentType-002");
                                documentTypeFormEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["DocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {filteredDocumentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("DetermineImportObjectDocumentType", state.steps[0].controls["DocumentType"].value)}
            </>
        )
    }

    function renderDetermineARImportObjectDocumentType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeARFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="ARDocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="ARDocumentType"
                            label="AR Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "ARDocumentType", "DetermineARImportObjectDocumentType");

                                documentTypeARFormEnterpriseComponent.newQuery();
                                documentTypeARFormEnterpriseComponent.setAttributeValue("USID", event.target.value);
                                documentTypeARFormEnterpriseComponent.setIDOCResponse("v1.DocumentType-002");
                                documentTypeARFormEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["ARDocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {documentTypeARBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("DetermineARImportObjectDocumentType", state.steps[0].controls["ARDocumentType"].value)}
            </>
        )
    }

    function renderDeterminePIImportObjectDocumentType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="DocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="DocumentType"
                            label="Object Document Type *"
                            onChange={(event) => {
                                changeHandler(event, "DocumentType", "DeterminePIImportObjectDocumentType");

                                documentTypeFormEnterpriseComponent.newQuery();
                                documentTypeFormEnterpriseComponent.setAttributeValue("USID", event.target.value);
                                documentTypeFormEnterpriseComponent.setIDOCResponse("v1.DocumentType-002");
                                documentTypeFormEnterpriseComponent.executeQuery(1, 50);
                            }}
                            value={state.steps[0].controls["DocumentType"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {documentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.USID} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("DeterminePIImportObjectDocumentType", state.steps[0].controls["DocumentType"].value)}
            </>
        )
    }

    function renderDetermineImportDocumentTypeForm() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFormFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="Form"
                            margin="dense"
                            fullWidth={true}
                            id="Form"
                            label={allowsBlankFormForSelectedDocumentType() ? "Form" : "Form *"}
                            onChange={(event) => {
                                changeHandler(event, "Form", "DetermineImportDocumentTypeForm")
                            }}
                            value={state.steps[0].controls["Form"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {renderFormSelectMenuItems(formBuffer)}
                        </TextField>
                    )}
                </div>
                {renderTransition("DetermineImportDocumentTypeForm", state.steps[0].controls["Form"].value)}
            </>
        )
    }

    function renderDetermineARImportDocumentTypeForm() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeARFormFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="ARForm"
                            margin="dense"
                            fullWidth={true}
                            id="ARForm"
                            label="Approval Request Form *"
                            onChange={(event) => {
                                changeHandler(event, "ARForm", "DetermineARImportDocumentTypeForm")
                            }}
                            value={state.steps[0].controls["ARForm"].value}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            {getFormTemplates(formBufferAR).map((template: any) => (
                                <MenuItem key={template.DocumentRevisionUSID} value={template.DocumentRevisionUSID}>
                                    {`${template.DocumentRevision.DocumentID} Rev ${template.DocumentRevision.DisplayRevision}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {renderTransition("DetermineARImportDocumentTypeForm", state.steps[0].controls["ARForm"].value)}
            </>
        )
    }

    function renderSelectUploadSource() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="UploadSource"
                        margin="dense"
                        fullWidth={true}
                        id="Form"
                        label="Upload Source *"
                        onChange={(event) => {
                            changeHandler(event, "UploadSource", "DetermineUploadSource")
                        }}
                        value={state.steps[0].controls["UploadSource"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        <MenuItem key={"Local Disk"} value={"LOCAL_DISK"}>
                            Local Disk
                        </MenuItem>
                        <MenuItem key={"GoogleDrive"} value={"GOOGLE_DRIVE"}>
                            Google Drive
                        </MenuItem>
                        <MenuItem key={"DropBox"} value={"DROPBOX"}>
                            Dropbox
                        </MenuItem>
                        <MenuItem key={"S3"} value={"S3"}>
                            S3
                        </MenuItem>
                    </TextField>
                </div>
                {renderTransition("DetermineUploadSource", state.steps[0].controls["UploadSource"].value)}
            </>
        )
    }

    function renderImport_ImportEnlilObject_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons() {
        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                <div style={{marginRight: "20px"}}>
                    <CCButton
                        className={css_buttons.redStandardButton}
                        onClick={() => cancelHandler()}>
                        Cancel
                    </CCButton>
                </div>
                <div style={{marginRight: "10px"}}>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveImportTemplateHandler("Save")}>
                        Save
                    </CCButton>
                </div>
                <div style={{marginRight: "10px"}}>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveImportTemplateHandler("Analyze")}>
                        Analyze
                    </CCButton>
                </div>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    animate={saveBackgroundOperation}
                    onClick={() => saveImportTemplateHandler("Execute")}>
                    Execute
                </CCButton>
            </div>
        )
    }


    function renderExportARImportDocumentTypeForm_CreateJobButtons() {
        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                <div style={{marginRight: "20px"}}>
                    <CCButton
                        className={css_buttons.redStandardButton}
                        onClick={() => cancelHandler()}>
                        Cancel
                    </CCButton>
                </div>
                <div style={{marginRight: "10px"}}>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveExportApprovalRequestTemplateHandler("Save")}>
                        Save
                    </CCButton>
                </div>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    animate={saveBackgroundOperation}
                    onClick={() => saveExportApprovalRequestTemplateHandler("Execute")}>
                    Save & Execute
                </CCButton>
            </div>
        )
    }

    function renderExecuteSubType() {
        let subTypeOptions = [
            "Enlil Service"
        ]

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="SubType"
                        margin="dense"
                        fullWidth={true}
                        id="SubType"
                        label="Sub Type *"
                        onChange={(event) => changeHandler(event, "SubType", "ExecuteSubType")}
                        value={state.steps[0].controls["SubType"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {subTypeOptions.map(option => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("ExecuteSubType", state.steps[0].controls["SubType"].value)}
            </>
        )
    }

    function renderExecuteEnlilService() {
        let objectOptions = [
            { value: "Company Duplication", label: "Copy Company Setup" },
            { value: "Copy Delta Forms", label: "Copy Delta Forms" },
        ]
        if (hasCompanyCreatePermission()) {
            objectOptions.push({ value: "Company Setup SQL", label: "Generate Company Setup SQL" })
        }
        if (hasCompanyDeletePermission()) {
            // objectOptions.push({ value: "Company Purge", label: "Generate Company Purge SQL" })
            objectOptions.push({ value: "Revision Cleanup", label: "Revision Cleanup" })
        }

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="Object"
                        margin="dense"
                        fullWidth={true}
                        id="Object"
                        label="Object *"
                        onChange={(event) => {
                            changeHandler(event, "Object", "ExecuteEnlilService");
                            if (event.target.value === "Revision Cleanup") {
                                documentTypeEnterpriseComponent.newQuery();
                                // @ts-ignore
                                documentTypeEnterpriseComponent.isDocumentQuery = true;
                                documentTypeEnterpriseComponent.setIDOCResponse("v1.DocumentType-001");
                                documentTypeEnterpriseComponent.executeQuery(1, 1000);
                            }
                            if (event.target.value === "Copy Delta Forms") {
                                setSelectedClonedCompanyUSID("");
                                setDeltaForms([]);
                                setSelectedDeltaFormDocumentUSIDs([]);
                                fetchClonedCompanies();
                            }
                        }}
                        value={state.steps[0].controls["Object"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {objectOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("ExecuteEnlilService", state.steps[0].controls["Object"].value)}
            </>
        )
    }

    async function uploadImageToS3(file: File, type: 'logo' | 'avatar') {
        const sessionContext = new SessionContext();
        const s3Client = new S3Client({
            region: "us-west-2",
            credentials: {
                accessKeyId: sessionContext.getS3AccessKeyId()!,
                secretAccessKey: sessionContext.getS3SecretAccessKey()!,
                sessionToken: sessionContext.getS3SessionToken()!,
            },
        });

        const ext = file.name.split('.').pop()?.toLowerCase() ?? 'png';
        const key = `jobs/companyDuplication/${sessionContext.getRealm()}/${jobUSID}/${type}.${ext}`;
        const bucket = `enlil-${process.env.REACT_APP_ENLIL_ENV}-attachments-t1`;

        const setStatus = type === 'logo' ? setLogoUploadStatus : setAvatarUploadStatus;
        const setKey = type === 'logo' ? setLogoS3Key : setAvatarS3Key;

        setStatus('uploading');
        try {
            const upload = new Upload({
                client: s3Client,
                params: { Bucket: bucket, Key: key, Body: file, ContentType: file.type },
            });
            await upload.done();
            setKey(key);
            setStatus('done');
        } catch {
            setStatus('error');
        }
    }

    function renderImageUploadRow(
        label: string,
        file: File | null,
        setFile: (f: File | null) => void,
        uploadStatus: 'idle' | 'uploading' | 'done' | 'error',
        inputRef: React.RefObject<HTMLInputElement>,
        type: 'logo' | 'avatar',
    ) {
        const statusColor = uploadStatus === 'done' ? '#00A044' : uploadStatus === 'error' ? '#FF0000' : '#7B8291';
        const statusLabel = uploadStatus === 'done' ? 'Done' : uploadStatus === 'error' ? 'Error' : uploadStatus === 'uploading' ? 'Uploading...' : 'Ready';

        return (
            <div style={{display: "flex", flexDirection: "row", alignItems: "center", margin: "0px 20px 20px 20px", padding: "8px 12px", border: "1px solid #BAC3CE", borderRadius: "5px", minHeight: "55px"}}>
                <IconButton
                    size="small"
                    disabled={uploadStatus === 'uploading'}
                    onClick={() => inputRef.current?.click()}
                    style={{marginRight: "12px"}}>
                    <CloudUpload style={{color: Grey6, width: "32px", height: "32px"}}/>
                </IconButton>
                <Typography style={{fontSize: "14px", fontWeight: 500, color: "#7B8291", minWidth: "120px"}}>{label}</Typography>
                {file && (
                    <>
                        <Typography style={{fontSize: "14px", fontWeight: 500, color: "#262837", marginLeft: "12px", flex: 1}}>{file.name}</Typography>
                        <Typography style={{fontSize: "14px", fontWeight: 500, color: statusColor, marginLeft: "12px"}}>{statusLabel}</Typography>
                    </>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    aria-label={`Upload ${label}`}
                    style={{display: "none"}}
                    onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        if (f) {
                            setFile(f);
                            uploadImageToS3(f, type);
                        }
                        e.target.value = '';
                    }}
                />
            </div>
        );
    }

    function renderExecuteCompanyDuplication() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        key="CompanyName"
                        margin="dense"
                        fullWidth={true}
                        id="CompanyName"
                        label="Company Name *"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        variant="outlined"
                        slotProps={{inputLabel: {shrink: true}}}
                    />
                </div>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        key="Acronym"
                        margin="dense"
                        fullWidth={true}
                        id="Acronym"
                        label="Acronym *"
                        value={acronym}
                        onChange={(e) => setAcronym(e.target.value)}
                        variant="outlined"
                        slotProps={{inputLabel: {shrink: true}}}
                    />
                </div>
                {renderImageUploadRow("Logo", logoFile, setLogoFile, logoUploadStatus, logoInputRef, 'logo')}
                {renderImageUploadRow("Avatar", avatarFile, setAvatarFile, avatarUploadStatus, avatarInputRef, 'avatar')}
                <div style={{display: "flex", flexDirection: "row", alignItems: "center", margin: "0px 20px 20px 20px", flexWrap: "wrap"}}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isDuplicateForms}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setIsDuplicateForms(checked);
                                    if (!checked) {
                                        setAllForms(true);
                                        setSelectedFormDocumentUSIDs([]);
                                        setCompanySetupFormBuffer([]);
                                    }
                                }}
                                id="IsDuplicateForms"
                            />
                        }
                        label="Duplicate Forms"
                    />
                    {isDuplicateForms && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allForms}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        setAllForms(checked);
                                        if (checked) {
                                            setSelectedFormDocumentUSIDs([]);
                                        } else {
                                            fetchCompanySetupForms();
                                        }
                                    }}
                                    id="AllForms"
                                />
                            }
                            label="All Forms"
                        />
                    )}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={addAllEmployees}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setAddAllEmployees(checked);
                                    if (!checked) {
                                        setPreserveOwner(false);
                                    }
                                }}
                                id="AddAllEmployees"
                            />
                        }
                        label="Add All Employees"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={preserveOwner}
                                onChange={(e) => setPreserveOwner(e.target.checked)}
                                id="PreserveOwner"
                                disabled={!addAllEmployees}
                            />
                        }
                        label="Preserve Owner"
                    />
                </div>
                {isDuplicateForms && !allForms && (
                    <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                        {companySetupFormFetchBackgroundOperation ? (
                            <img width="35" height="35" src={spinner} alt="spinner" />
                        ) : (
                            <TextField
                                select
                                key="SelectedForms"
                                margin="dense"
                                fullWidth={true}
                                id="SelectedForms"
                                label="Forms"
                                value={selectedFormDocumentUSIDs}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSelectedFormDocumentUSIDs(
                                        typeof value === "string" ? value.split(",") : value,
                                    );
                                }}
                                variant="outlined"
                                slotProps={{
                                    select: {
                                        multiple: true,
                                        renderValue: (selected: unknown) => {
                                            const selectedIds = selected as string[];
                                            const options = getCompanySetupFormOptions();
                                            return selectedIds
                                                .map((id) => options.find((opt) => opt.documentUSID === id)?.label ?? id)
                                                .join(", ");
                                        },
                                    },
                                    inputLabel: {shrink: true},
                                }}
                                helperText={
                                    getCompanySetupFormOptions().length === 0
                                        ? "No RELEASED form documents found (document type group FORM)"
                                        : "Select one or more forms to copy (DAM/AR forms are always copied)"
                                }
                            >
                                {getCompanySetupFormOptions().map((option) => (
                                    <MenuItem key={option.documentUSID} value={option.documentUSID}>
                                        <Checkbox checked={selectedFormDocumentUSIDs.indexOf(option.documentUSID) > -1} />
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </div>
                )}
                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                    <div style={{marginRight: "20px"}}>
                        <CCButton
                            className={css_buttons.redStandardButton}
                            onClick={() => cancelHandler()}>
                            Cancel
                        </CCButton>
                    </div>
                    <div style={{marginRight: "10px"}}>
                        <CCButton
                            className={css_buttons.blueStandardButton}
                            animate={saveBackgroundOperation}
                            onClick={() => saveExecuteCompanyDuplicationHandler("Save")}>
                            Save
                        </CCButton>
                    </div>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveExecuteCompanyDuplicationHandler("Execute")}>
                        Save & Execute
                    </CCButton>
                </div>
            </>
        )
    }

    function renderExecuteCompanySetupSql() {
        if (!hasCompanyCreatePermission()) {
            return null;
        }

        const sessionContext = new SessionContext();
        const currentCompanyName = sessionContext.getCompanyName() || "";
        const currentRealm = sessionContext.getRealm() || "";

        return (
            <>
                <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 20px 20px", gap: "8px"}}>
                    <Typography style={{fontSize: "14px", color: "#7B8291"}}>
                        Generate a company-setup SQL script for the current company (review and apply
                        manually). This uses the same duplication pipeline as Copy Company Setup, but
                        exports SQL instead of creating a live tenant.
                    </Typography>
                    <Typography style={{fontSize: "14px", color: "#7B8291"}}>
                        Source company: <strong>{currentCompanyName || "(unknown)"}</strong>
                        {currentRealm ? ` (${currentRealm})` : ""}
                    </Typography>
                    <Typography style={{fontSize: "14px", color: "#7B8291"}}>
                        Job name is taken from the Name field above. The script will be uploaded to S3
                        and emailed when ready.
                    </Typography>
                </div>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                    <div style={{marginRight: "20px"}}>
                        <CCButton
                            className={css_buttons.redStandardButton}
                            onClick={() => cancelHandler()}>
                            Cancel
                        </CCButton>
                    </div>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        disabled={!hasCompanyCreatePermission()}
                        onClick={() => saveExecuteCompanySetupSqlHandler("Execute")}>
                        Save & Generate SQL
                    </CCButton>
                </div>
            </>
        )
    }

    function renderExecuteCopyDeltaForms() {
        const canExecute = !!selectedClonedCompanyUSID && selectedDeltaFormDocumentUSIDs.length > 0;

        return (
            <>
                <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 12px 20px", gap: "6px"}}>
                    <Typography style={{fontSize: "14px", color: "#7B8291"}}>
                        Copy RELEASED forms that exist in this company but are missing from a cloned
                        company (matched by Document ID). Form Owner is retained when that employee
                        exists in the clone; otherwise Server Automation is used.
                    </Typography>
                </div>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {clonedCompaniesLoading ? (
                        <img width="35" height="35" src={spinner} alt="spinner" />
                    ) : (
                        <TextField
                            select
                            key="ClonedCompany"
                            margin="dense"
                            fullWidth={true}
                            id="ClonedCompany"
                            label="Cloned Company *"
                            value={selectedClonedCompanyUSID}
                            onChange={(event) => {
                                const usid = event.target.value;
                                setSelectedClonedCompanyUSID(usid);
                                setSelectedDeltaFormDocumentUSIDs([]);
                                setDeltaForms([]);
                                if (usid) {
                                    fetchDeltaForms(usid);
                                }
                            }}
                            variant="outlined"
                            slotProps={{inputLabel: {shrink: true}}}
                            helperText={
                                clonedCompanies.length === 0
                                    ? "No companies cloned from the current company were found"
                                    : undefined
                            }
                        >
                            {clonedCompanies.map((company) => (
                                <MenuItem key={company.USID} value={company.USID}>
                                    {company.CompanyAcronym
                                        ? `${company.Name} (${company.CompanyAcronym})`
                                        : company.Name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                {selectedClonedCompanyUSID ? (
                    <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                        {deltaFormsLoading ? (
                            <img width="35" height="35" src={spinner} alt="spinner" />
                        ) : (
                            <TextField
                                select
                                key="DeltaForms"
                                margin="dense"
                                fullWidth={true}
                                id="DeltaForms"
                                label="Forms to Copy *"
                                value={selectedDeltaFormDocumentUSIDs}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSelectedDeltaFormDocumentUSIDs(
                                        typeof value === "string" ? value.split(",") : value,
                                    );
                                }}
                                variant="outlined"
                                slotProps={{
                                    select: {
                                        multiple: true,
                                        renderValue: (selected: unknown) => {
                                            const selectedIds = selected as string[];
                                            return selectedIds
                                                .map((id) => deltaForms.find((opt) => opt.DocumentUSID === id)?.Label ?? id)
                                                .join(", ");
                                        },
                                    },
                                    inputLabel: {shrink: true},
                                }}
                                helperText={
                                    deltaForms.length === 0
                                        ? "No missing RELEASED forms (parent and clone are in sync)"
                                        : "Select one or more forms present in this company but not in the clone"
                                }
                            >
                                {deltaForms.map((option) => (
                                    <MenuItem key={option.DocumentUSID} value={option.DocumentUSID}>
                                        <Checkbox checked={selectedDeltaFormDocumentUSIDs.indexOf(option.DocumentUSID) > -1} />
                                        {option.Label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </div>
                ) : null}
                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                    <div style={{marginRight: "20px"}}>
                        <CCButton
                            className={css_buttons.redStandardButton}
                            onClick={() => cancelHandler()}>
                            Cancel
                        </CCButton>
                    </div>
                    <div style={{marginRight: "10px"}}>
                        <CCButton
                            className={css_buttons.blueStandardButton}
                            animate={saveBackgroundOperation}
                            disabled={!canExecute}
                            onClick={() => saveExecuteCopyDeltaFormsHandler("Save")}>
                            Save
                        </CCButton>
                    </div>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        disabled={!canExecute}
                        onClick={() => saveExecuteCopyDeltaFormsHandler("Execute")}>
                        Save & Execute
                    </CCButton>
                </div>
            </>
        )
    }

    function renderExecuteRevisionCleanup() {
        if (!hasCompanyDeletePermission()) {
            return null;
        }

        const cleanupModeOptions = [
            { value: "BOTH", label: "Both" },
            { value: "MULTIPLE_RELEASED", label: "Fix Multiple Released Revisions" },
            { value: "VERSION_RECONCILE", label: "Fix Out of Sequence Version Numbers" },
            { value: "ENSURE_RELEASED_TIP", label: "Ensure Released Tip (promote Superseded tip)" },
        ];

        return (
            <>
                <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 20px 20px", gap: "8px"}}>
                    <Typography style={{fontSize: "14px", color: "#7B8291"}}>
                        Fixes revision data for the current company. Choose which fix to run. Leave Document
                        Type and Document Numbers blank to run for all documents.
                    </Typography>
                </div>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="CleanupMode"
                        margin="dense"
                        fullWidth={true}
                        id="CleanupMode"
                        label="Cleanup Mode *"
                        onChange={(event) => setRevisionCleanupMode(event.target.value)}
                        value={revisionCleanupMode}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {cleanupModeOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    {documentTypeFetchBackgroundOperation ? <img width="35" height="35" src={spinner} alt="spinner" /> : (
                        <TextField
                            select
                            key="DocumentType"
                            margin="dense"
                            fullWidth={true}
                            id="DocumentType"
                            label="Document Type (optional)"
                            onChange={(event) => changeHandler(event, "DocumentType", "ExecuteRevisionCleanup")}
                            value={state.steps[0].controls["DocumentType"]?.value || ""}
                            variant="outlined"
                            slotProps={{
                                input: {},
                                inputLabel: {shrink: true}
                            }}>
                            <MenuItem key="__all__" value="">
                                All document types
                            </MenuItem>
                            {documentTypeBuffer.map((element: any) => (
                                <MenuItem key={element.get("USID")} value={element.get("USID")}>
                                    {`${element.get("DocumentTypeName")} (${element.get("DocumentTypeAcronym")})`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </div>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        margin="dense"
                        fullWidth={true}
                        id="RevisionCleanupDocumentIds"
                        label="Document Numbers (optional, comma-separated; wildcards * ? % supported)"
                        placeholder="CS-100*, PART-001"
                        helperText="Leave blank to run for all documents (still scoped by Document Type if set)."
                        value={revisionCleanupDocumentIds}
                        onChange={(e) => setRevisionCleanupDocumentIds(e.target.value)}
                        variant="outlined"
                        slotProps={{
                            inputLabel: {shrink: true}
                        }}
                    />
                </div>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                    <div style={{marginRight: "20px"}}>
                        <CCButton
                            className={css_buttons.redStandardButton}
                            onClick={() => cancelHandler()}>
                            Cancel
                        </CCButton>
                    </div>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveExecuteRevisionCleanupHandler("Execute")}>
                        Save & Execute
                    </CCButton>
                </div>
            </>
        )
    }

    function renderExecuteCompanyPurge() {
        const sessionContext = new SessionContext();
        const currentCompanyName = sessionContext.getCompanyName() || "";
        const currentRealm = sessionContext.getRealm() || "";
        const nameMatches =
            purgeConfirmedName.trim().length > 0 &&
            purgeConfirmedName.trim().toLowerCase() === currentCompanyName.trim().toLowerCase();

        return (
            <>
                <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 20px 20px", gap: "8px"}}>
                    <Typography style={{fontSize: "14px", color: "#B00020", fontWeight: 600}}>
                        Generate a tenant-wipe SQL script (review and apply manually). This job does not
                        delete any data by itself.
                    </Typography>
                    <Typography style={{fontSize: "14px", color: "#7B8291"}}>
                        Target company: <strong>{currentCompanyName || "(unknown)"}</strong>
                        {currentRealm ? ` (${currentRealm})` : ""}
                    </Typography>
                    <Typography style={{fontSize: "14px", color: "#7B8291"}}>
                        Type the exact company name below to enable SQL generation. The script will be
                        uploaded to S3 and emailed when ready. S3 cleanup steps are included as comments.
                    </Typography>
                </div>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        key="PurgeConfirmedName"
                        margin="dense"
                        fullWidth={true}
                        id="PurgeConfirmedName"
                        label="Confirm company name *"
                        value={purgeConfirmedName}
                        onChange={(e) => setPurgeConfirmedName(e.target.value)}
                        variant="outlined"
                        slotProps={{inputLabel: {shrink: true}}}
                    />
                </div>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                    <div style={{marginRight: "20px"}}>
                        <CCButton
                            className={css_buttons.redStandardButton}
                            onClick={() => cancelHandler()}>
                            Cancel
                        </CCButton>
                    </div>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        disabled={!nameMatches || !hasCompanyDeletePermission()}
                        onClick={() => saveExecuteCompanyPurgeHandler("Execute")}>
                        Save & Generate SQL
                    </CCButton>
                </div>
            </>
        )
    }

    function getFormTemplates(buffer: Map<string, any>[]) {
        const templates: any[] = [];

        buffer.forEach((element: any) => {
            const formTemplates = element.get("ListOfFormTemplate")?.FormTemplate;
            if (Array.isArray(formTemplates)) {
                formTemplates.forEach((template: any) => {
                    if (template?.DocumentRevisionUSID && template?.DocumentRevision) {
                        templates.push(template);
                    }
                });
            }
        });

        return templates;
    }

    function getCompanySetupFormOptions(): Array<{documentUSID: string, label: string}> {
        // Match SQL: document_type.group = FORM → document → RELEASED revision (latest version per doc).
        const byDocumentUSID = new Map<string, {documentUSID: string, label: string, version: number}>();
        companySetupFormBuffer.forEach((row: any) => {
            const documentUSID = row.get("DocumentUSID");
            const documentID = row.get("DocumentID");
            if (!documentUSID || !documentID) {
                return;
            }
            const versionRaw = row.get("Version");
            const version = versionRaw == null || versionRaw === "" ? -1 : Number(versionRaw);
            const existing = byDocumentUSID.get(documentUSID);
            if (existing && existing.version >= version) {
                return;
            }
            const displayRevision = row.get("DisplayRevision") ?? "";
            byDocumentUSID.set(documentUSID, {
                documentUSID,
                version: Number.isFinite(version) ? version : -1,
                label: displayRevision
                    ? `${documentID} Rev ${displayRevision}`
                    : `${documentID}`,
            });
        });
        return Array.from(byDocumentUSID.values())
            .map(({documentUSID, label}) => ({documentUSID, label}))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    function fetchCompanySetupForms() {
        // FORM document types' documents with RELEASED revisions (not output-junction FormTemplates).
        companySetupFormEnterpriseComponent.newQuery();
        companySetupFormEnterpriseComponent.setAttributeValue("DocumentTypeGroup", "FORM");
        companySetupFormEnterpriseComponent.setAttributeValue("Status", "RELEASED");
        companySetupFormEnterpriseComponent.setIDOCResponse("v1.DocumentRevision-001");
        companySetupFormEnterpriseComponent.executeQuery(1, 1000);
    }

    async function fetchClonedCompanies() {
        setClonedCompaniesLoading(true);
        setClonedCompanies([]);
        try {
            const response = await EnlilAxiosRestAPI.get("t3/v1/cloned_companies");
            const companies = response?.data?.Companies;
            setClonedCompanies(Array.isArray(companies) ? companies : []);
        } catch (error) {
            console.error("Failed to load cloned companies", error);
            setClonedCompanies([]);
        } finally {
            setClonedCompaniesLoading(false);
        }
    }

    async function fetchDeltaForms(destinationCompanyUSID: string) {
        setDeltaFormsLoading(true);
        setDeltaForms([]);
        try {
            const response = await EnlilAxiosRestAPI.get("t3/v1/delta_forms", {
                params: {DestinationCompanyUSID: destinationCompanyUSID},
            });
            const forms = response?.data?.Forms;
            setDeltaForms(Array.isArray(forms) ? forms : []);
        } catch (error) {
            console.error("Failed to load delta forms", error);
            setDeltaForms([]);
        } finally {
            setDeltaFormsLoading(false);
        }
    }

    function getSelectedDocumentTypeRecord(): Map<string, any> | undefined {
        const documentTypeUsid = state.steps[0].controls["DocumentType"].value;
        if (!documentTypeUsid) {
            return undefined;
        }

        return documentTypeBuffer.find((element: any) => element.get("USID") === documentTypeUsid);
    }

    function allowsBlankFormForSelectedDocumentType(): boolean {
        const selectedType = getSelectedDocumentTypeRecord();
        if (!selectedType) {
            return false;
        }

        if (selectedType.get("Group") === "OTHER") {
            return true;
        }

        return getFormTemplates(formBuffer).length === 0;
    }

    function renderFormSelectMenuItems(buffer: Map<string, any>[]) {
        const menuItems: any[] = [];

        if (allowsBlankFormForSelectedDocumentType()) {
            menuItems.push(
                <MenuItem key={BLANK_FORM_VALUE} value={BLANK_FORM_VALUE}>
                    (No form)
                </MenuItem>,
            );
        }

        getFormTemplates(buffer).forEach((template: any) => {
            menuItems.push(
                <MenuItem key={template.DocumentRevisionUSID} value={template.DocumentRevisionUSID}>
                    {`${template.DocumentRevision.DocumentID} Rev ${template.DocumentRevision.DisplayRevision}`}
                </MenuItem>,
            );
        });

        return menuItems;
    }

    function renderSelectEquipmentDownloadVolume() {
        let options = [
            "1+ Equipment Objects",
            "All Equipment Objects"
        ]

        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="DownloadVolume"
                        margin="dense"
                        fullWidth={true}
                        id="DownloadVolume"
                        label="Download Volume *"
                        onChange={(event) => changeHandler(event, "DownloadVolume", "SelectEquipmentDownloadVolume")}
                        value={state.steps[0].controls["DownloadVolume"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        {options.map(option => (
                            <MenuItem key={option} value={option.toUpperCase()}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                {renderTransition("SelectEquipmentDownloadVolume", state.steps[0].controls["DownloadVolume"].value)}
            </>
        )
    }

    function formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    function renderUploadSpreadsheetFromLocalDisk() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 20px 20px", padding: "12px", border: "1px solid #E0E0E0", borderRadius: "5px", backgroundColor: "#F5F7FA"}}>
                    <div style={{display: "flex", flexDirection: "column", marginBottom: "12px"}}>
                        <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                            <CloudUpload style={{color: "#1498D8", width: "20px", height: "20px", marginRight: "8px"}}/>
                            <Typography style={{fontSize: "14px", fontWeight: 500, color: "#262837"}}>
                                {isAttachmentImportJob() ? "Upload Spreadsheet and Zip" : "Upload Attachment"}
                            </Typography>
                        </div>
                        {isAttachmentImportJob() ? (
                            <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291", marginTop: "4px"}}>
                                Upload one .xlsx/.xlsm file and one .zip file. You can upload them one at a time.
                            </Typography>
                        ) : null}
                    </div>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "12px", flexWrap: "wrap"}}>
                        <IconButton
                            className={css_buttons.whiteStandardButton} style={{cursor: "pointer"}} size="small"
                            onClick={() => {
                                setShowUploadSpreadsheet(true)
                            }}>
                            <Backup style={{color: Grey6, width: "40px", height: "40px"}}/>
                        </IconButton>
                        {selectedFiles.map((file: any, index) => (
                            <div key={index } style={{display: "flex", flexDirection: "column", marginTop: "6px", marginRight: "20px"}}>
                                <div key={file.name + index} style={{display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                                    <div key={file.name + index} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", width: "500px"}}>
                                        <div key={1} style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                            <div style={{width: "33px", height: "33px", backgroundColor: "#EDEFF3", borderRadius: "5px", margin: "5px 5px 5px 10px"}}>
                                                <TextSnippet sx={{color: "#A8A9AF", width: "18px", height: "18px", marginTop: "7px", marginLeft: "8px"}}/>
                                            </div>
                                            <div style={{display: "flex", flexDirection: "column"}}>
                                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "3px"}}>
                                                    <Typography style={{fontSize: "14px", fontWeight: 500, color: "#262837"}}>{file.name}</Typography>
                                                </div>
                                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginTop: "-3px"}}>
                                                    <Typography style={{fontFamily: "courier", fontSize: "13px", fontWeight: 400, color: "#262837"}}>{formatFileSize(file.size)}</Typography>
                                                    <Typography style={{fontFamily: "courier", fontSize: "13px", fontWeight: 400, color: "#262837"}}>&nbsp;&#183;&nbsp;</Typography>
                                                    <Typography style={{fontSize: "14px", fontWeight: 500, color: "#00A044"}}>Done</Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {renderTransition(
                    state.steps[0].controls["Type"]?.value === "Purge"
                        ? "PurgeDocumentsUploadSpreadsheet"
                        : "UploadSpreadsheetFromLocalDisk",
                    (isAttachmentImportJob()
                        ? attachmentImportFilesReady(selectedFiles)
                        : selectedFiles.length > 0) ? "True" : ""
                )}
            </>
        )
    }

    function renderUploadTrainingQuizSpreadsheet() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "column", margin: "0px 20px 20px 20px", padding: "12px", border: "1px solid #E0E0E0", borderRadius: "5px", backgroundColor: "#F5F7FA"}}>
                    <div style={{display: "flex", flexDirection: "column", marginBottom: "12px"}}>
                        <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                            <CloudUpload style={{color: "#1498D8", width: "20px", height: "20px", marginRight: "8px"}}/>
                            <Typography style={{fontSize: "14px", fontWeight: 500, color: "#262837"}}>
                                Upload Training Quiz Spreadsheet
                            </Typography>
                        </div>
                        <Typography style={{fontSize: "12px", fontWeight: 400, color: "#7B8291", marginTop: "4px"}}>
                            Upload one .xlsx file with Quiz, Questions, and Options sheets.
                        </Typography>
                    </div>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "12px", flexWrap: "wrap"}}>
                        <IconButton
                            className={css_buttons.whiteStandardButton} style={{cursor: "pointer"}} size="small"
                            onClick={() => {
                                setShowUploadSpreadsheet(true)
                            }}>
                            <Backup style={{color: Grey6, width: "40px", height: "40px"}}/>
                        </IconButton>
                        {selectedFiles.map((file: any, index) => (
                            <div key={index} style={{display: "flex", flexDirection: "column", marginTop: "6px", marginRight: "20px"}}>
                                <Typography style={{fontSize: "14px", fontWeight: 500, color: "#262837"}}>{file.name}</Typography>
                                <Typography style={{fontFamily: "courier", fontSize: "13px", fontWeight: 400, color: "#262837"}}>{formatFileSize(file.size)}</Typography>
                            </div>
                        ))}
                    </div>
                </div>
                {renderTransition(
                    "UploadTrainingQuizSpreadsheet",
                    selectedFiles.length > 0 ? "True" : ""
                )}
            </>
        )
    }

    function renderCreateTrainingQuizJobButtons() {
        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                <div style={{marginRight: "20px"}}>
                    <CCButton
                        className={css_buttons.redStandardButton}
                        onClick={() => cancelHandler()}>
                        Cancel
                    </CCButton>
                </div>
                <div style={{marginRight: "10px"}}>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveImportTemplateHandler("Save")}>
                        Save
                    </CCButton>
                </div>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    animate={saveBackgroundOperation}
                    onClick={() => saveImportTemplateHandler("Execute")}>
                    Execute
                </CCButton>
            </div>
        )
    }

    function renderExportTrainingQuizTemplate_CreateJobButtons() {
        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                <div style={{marginRight: "20px"}}>
                    <CCButton
                        className={css_buttons.redStandardButton}
                        onClick={() => cancelHandler()}>
                        Cancel
                    </CCButton>
                </div>
                <div style={{marginRight: "10px"}}>
                    <CCButton
                        className={css_buttons.blueStandardButton}
                        animate={saveBackgroundOperation}
                        onClick={() => saveExportTrainingQuizTemplateHandler("Save")}>
                        Save
                    </CCButton>
                </div>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    animate={saveBackgroundOperation}
                    onClick={() => saveExportTrainingQuizTemplateHandler("Execute")}>
                    Save & Execute
                </CCButton>
            </div>
        )
    }

    function renderExecutePurgeCreateJobButtons() {
        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end", margin: "0px 20px 20px 20px"}}>
                <div style={{marginRight: "20px"}}>
                    <CCButton
                        className={css_buttons.redStandardButton}
                        onClick={() => cancelHandler()}>
                        Cancel
                    </CCButton>
                </div>
                <CCButton
                    className={css_buttons.blueStandardButton}
                    animate={saveBackgroundOperation}
                    onClick={() => saveExecutePurgeDocumentsHandler("Execute")}>
                    Execute
                </CCButton>
            </div>
        )
    }

    function renderTransition(directive: string, selection: string): any {
        if (selection === "") return null;

        // @ts-ignore
        let transition = transitions[directive][selection];
        if (transition === undefined) {
            selection = "ANY"
            // @ts-ignore
            transition = transitions[directive][selection];
        }

        // Safety check: if transition is still undefined, the directive/selection doesn't exist
        if (transition === undefined) return null;

        let r = null;
        if (directive === "DetermineImportDocumentTypeForm" && breadcrumb.get("ImportEnlilObject") === "PART") {
            r = transitions["DetermineARImportObjectDocumentType"]["Renderer"]
        } else if (directive === "DetermineImportDocumentTypeForm" && breadcrumb.get("ImportEnlilObject") === "EQUIPMENT_SPECIFICATION") {
            r = transitions["DetermineARImportObjectDocumentType"]["Renderer"]
        } else if (directive === "DetermineImportDocumentTypeForm" && breadcrumb.get("ImportEnlilObject") === "DOCUMENT") {
            r = transitions["DetermineARImportObjectDocumentType"]["Renderer"]
        } else if (directive === "DetermineARImportDocumentTypeForm" && state.steps[0].controls["Type"].value === "Export") {
            r = transitions["ExportARImportDocumentTypeForm"]["Renderer"]
        } else {
            // @ts-ignore
            const nextState = transitions[directive][selection];
            if (nextState === undefined) return null;
            // @ts-ignore
            r = transitions[nextState]?.["Renderer"];
        }

        // Safety check: if renderer is not found, return null
        if (!r) return null;

        if (!r || !(funcs as any)[r]) return null;
        // @ts-ignore
        return funcs[r]();
    }

    function renderType() {
        return (
            <>
                <div style={{display: "flex", flexDirection: "row", margin: "0px 20px 20px 20px"}}>
                    <TextField
                        select
                        key="Type"
                        margin="dense"
                        fullWidth={true}
                        id="Type"
                        label="Type *"
                        onChange={(event) => {
                            changeHandler(event, "Type", "Type")
                        }}
                        value={state.steps[0].controls["Type"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}>
                        <MenuItem key={1} value="Import">
                            Import
                        </MenuItem>
                        <MenuItem key={2} value="Export">
                            Export
                        </MenuItem>
                        <MenuItem
                            key={3}
                            value="Execute"
                            disabled={!hasCompanyCreatePermission()}
                            title={!hasCompanyCreatePermission() ? "You don't have permission to execute jobs. Required groups: COMPANY_CREATE or COMPANY_BOOTSTRAP" : ""}
                        >
                            Execute
                        </MenuItem>
                        <MenuItem
                            key={4}
                            value="Purge">
                            Purge
                        </MenuItem>
                    </TextField>
                </div>
                {renderTransition("Type", state.steps[0].controls["Type"].value)}
            </>
        )
    }

    function render() {
        return (
            <div style={{display: "flex", flexDirection: "column"}}>
                <div style={{display: "flex", flexDirection: "row", margin: "20px 20px 20px 20px"}}>
                    <TextField
                        key="Name"
                        margin="dense"
                        fullWidth={true}
                        helperText={"Job name must be 1-128 characters. The first character must be alphanumeric. Valid characters are a-z, A-Z, 0-9, hyphens (-), and underscores (_)."}
                        id="Name"
                        label="Name *"
                        onChange={(event) => changeHandler(event, "Name", "Name")}
                        value={state.steps[0].controls["Name"].value}
                        variant="outlined"
                        slotProps={{
                            input: {},
                            inputLabel: {shrink: true}
                        }}
                    />
                </div>
                {state.steps[0].controls["Name"].value !== "" ? renderType() : null}
            </div>
        )
    }

    function saveImportTemplateHandler(action: string) {
        let sessionContext = new SessionContext();

        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID",  jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name",  state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type",  state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType",  state.steps[0].controls["SubType"].value)

        const jobPathPrefix = "jobs/" + sessionContext.getRealm() + "/" + jobUSID + "/";
        let specification: Record<string, string>;

        if (isAttachmentImportJob()) {
            const spreadsheetFile = findAttachmentSpreadsheetFile(selectedFiles);
            const zipFile = findAttachmentZipFile(selectedFiles);
            if (!spreadsheetFile || !zipFile) {
                setSaveBackgroundOperation(false);
                return;
            }

            specification = {
                "Action": action,
                "Object": "",
                "Spreadsheet": jobPathPrefix + spreadsheetFile.name,
                "ZipFile": jobPathPrefix + zipFile.name,
                "UploadSource": "Local Disk",
            };
        } else if (isTrainingQuizImportJob()) {
            // @ts-ignore
            let name = selectedFiles[0].name;
            specification = {
                "Action": action,
                "Object": "TRAINING_QUIZ",
                "Spreadsheet": jobPathPrefix + name,
                "UploadSource": "Local Disk",
            };
        } else {
            // @ts-ignore
            let name = selectedFiles[0].name;

            specification = {
                "Action": action,
                "Object": state.steps[0].controls["Object"].value,
                "Spreadsheet": jobPathPrefix + name,
                "UploadSource": "Local Disk",
                "DocumentTypeUSID": state.steps[0].controls["DocumentType"].value,
                "DocumentTypeGroup": state.steps[0].controls["DocumentTypeGroup"]?.value ?? "",
                "FormDocumentRevisionUSID": normalizeFormDocumentRevisionUSID(state.steps[0].controls["Form"].value),
                "ARDocumentTypeUSID": state.steps[0].controls["ARDocumentType"].value,
                "ARFormDocumentRevisionUSID": state.steps[0].controls["ARForm"].value
            };
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        console.log(etlJobEnterpriseComponent)
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExportEnlilDataHandler(action: string) {
        if (!hasExportEnlilDataPermission()) {
            return
        }

        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID",  jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name",  state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type",  state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType",  state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": state.steps[0].controls["Object"].value,
            "ExportWithServerAutomation": exportWithServerAutomation,
            "ExportLatestReleasedOnly": exportLatestReleasedOnly,
            "ExportDocumentIds": exportDocumentIds.trim(),
            "ExportReleasedAtStart": exportReleasedAtStart,
            "ExportReleasedAtEnd": exportReleasedAtEnd,
            "ExportOnlyWithTasks":
                state.steps[0].controls["Object"].value === "APPROVAL_REQUEST"
                    ? exportOnlyWithTasks
                    : false,
            "ExportOnlyWithMaterialDisposition":
                state.steps[0].controls["Object"].value === "APPROVAL_REQUEST"
                    ? exportOnlyWithMaterialDisposition
                    : false,
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExportTemplateHandler(action: string) {
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID",  jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name",  state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type",  state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType",  state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": state.steps[0].controls["Object"].value,
            "DocumentTypeUSID": state.steps[0].controls["DocumentType"].value,
            "DocumentTypeGroup": state.steps[0].controls["DocumentTypeGroup"]?.value ?? "",
            "FormDocumentRevisionUSID": normalizeFormDocumentRevisionUSID(state.steps[0].controls["Form"].value)
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExportTrainingQuizTemplateHandler(action: string) {
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID", jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name", state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type", state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType", state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": "TRAINING_QUIZ",
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExportApprovalRequestTemplateHandler(action: string) {
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID",  jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name",  state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type",  state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType",  state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": "APPROVAL_REQUEST",
            "ARDocumentTypeUSID": state.steps[0].controls["ARDocumentType"].value,
            "ARFormDocumentRevisionUSID": state.steps[0].controls["ARForm"].value
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExportEquipmentTemplateHandler(action: string) {
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID",  jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name",  state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type",  state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType",  state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": state.steps[0].controls["Object"].value,
            "DocumentTypeUSID": state.steps[0].controls["DocumentType"].value,
            "FormDocumentRevisionUSID": state.steps[0].controls["Form"].value,
            "CalibrationDocumentTypeUSID": state.steps[0].controls["CalibrationDocumentType"].value,
            "PreventativeMaintenanceDocumentTypeUSID": state.steps[0].controls["PreventativeMaintenanceDocumentType"].value,
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExecutePurgeDocumentsHandler(action: string) {
        let sessionContext = new SessionContext();

        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID",  jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name",  state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type",  "Execute")
        etlJobEnterpriseComponent.setAttributeValue("SubType",  "Purge Documents")

        // @ts-ignore
        let name = selectedFiles[0].name

        let specification = {
            "Action": action,
            "Object": "DOCUMENT",
            "Spreadsheet": "jobs/" + sessionContext.getRealm() + "/" + jobUSID + "/" + name,
            "UploadSource": "Local Disk",
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExecuteCompanyDuplicationHandler(action: string) {
        let sessionContext = new SessionContext();
        const resolvedCompanyName = companyName.trim();
        const resolvedAcronym = acronym.trim();
        
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID",  jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name",  state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type",  state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType",  state.steps[0].controls["SubType"].value)
        let specification: Record<string, any> = {
            "Action": action,
            "Object": "COMPANY DUPLICATION",
            "CompanyUSID": sessionContext.getRealm(),
            "Realm": sessionContext.getRealm(),
            "EmployeeUSID": sessionContext.getEmployeeUSID(),
            "CompanyName": resolvedCompanyName,
            "Acronym": resolvedAcronym,
            "LogoS3Key": logoS3Key,
            "AvatarS3Key": avatarS3Key,
            "IsDuplicateForms": isDuplicateForms,
            "AddAllEmployees": addAllEmployees,
            "PreserveOwner": preserveOwner,
            "GenerateSqlScript": false,
        }
        // All Forms checked (or Duplicate Forms off) → omit selection; backend clones all/none as today.
        if (isDuplicateForms && !allForms) {
            specification["SelectedFormDocumentUSIDs"] = selectedFormDocumentUSIDs;
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExecuteCompanySetupSqlHandler(action: string) {
        if (!hasCompanyCreatePermission()) {
            return;
        }

        let sessionContext = new SessionContext();
        // Same T1/T3 duplication pipeline as Copy Company Setup, SQL export only.
        // Name/acronym default from the current session company (backend also resolves blanks).
        const resolvedCompanyName = sessionContext.getCompanyName() || "";
        const resolvedAcronym = "";

        setSaveBackgroundOperation(true);
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID", jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name", state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type", state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType", state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": "COMPANY DUPLICATION",
            "CompanyUSID": sessionContext.getRealm(),
            "Realm": sessionContext.getRealm(),
            "EmployeeUSID": sessionContext.getEmployeeUSID(),
            "CompanyName": resolvedCompanyName,
            "Acronym": resolvedAcronym,
            "LogoS3Key": "",
            "AvatarS3Key": "",
            "IsDuplicateForms": true,
            "AddAllEmployees": false,
            "PreserveOwner": false,
            "GenerateSqlScript": true,
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExecuteCopyDeltaFormsHandler(action: string) {
        if (!selectedClonedCompanyUSID || selectedDeltaFormDocumentUSIDs.length === 0) {
            return;
        }

        let sessionContext = new SessionContext();
        setSaveBackgroundOperation(true);
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID", jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name", state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type", state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType", state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": "COPY DELTA FORMS",
            "CompanyUSID": sessionContext.getRealm(),
            "Realm": sessionContext.getRealm(),
            "EmployeeUSID": sessionContext.getEmployeeUSID(),
            "DestinationCompanyUSID": selectedClonedCompanyUSID,
            "SelectedFormDocumentUSIDs": selectedDeltaFormDocumentUSIDs,
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExecuteCompanyPurgeHandler(action: string) {
        if (!hasCompanyDeletePermission()) {
            return;
        }
        let sessionContext = new SessionContext();
        const currentCompanyName = sessionContext.getCompanyName() || "";
        if (purgeConfirmedName.trim().toLowerCase() !== currentCompanyName.trim().toLowerCase()) {
            return;
        }

        setSaveBackgroundOperation(true);
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID", jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name", state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type", state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType", state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": state.steps[0].controls["Object"].value.toUpperCase(),
            "CompanyUSID": sessionContext.getRealm(),
            "Realm": sessionContext.getRealm(),
            "EmployeeUSID": sessionContext.getEmployeeUSID(),
            "ConfirmedName": purgeConfirmedName.trim(),
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    function saveExecuteRevisionCleanupHandler(action: string) {
        if (!hasCompanyDeletePermission()) {
            return;
        }
        let sessionContext = new SessionContext();
        const documentTypeUsid = (state.steps[0].controls["DocumentType"]?.value || "").trim();
        const cleanupMode = (revisionCleanupMode || "BOTH").trim().toUpperCase() || "BOTH";
        const documentIds = (revisionCleanupDocumentIds || "").trim();

        setSaveBackgroundOperation(true);
        etlJobEnterpriseComponent.newRecord();
        etlJobEnterpriseComponent.setAttributeValue("USID", jobUSID)
        etlJobEnterpriseComponent.setAttributeValue("Name", state.steps[0].controls["Name"].value)
        etlJobEnterpriseComponent.setAttributeValue("Type", state.steps[0].controls["Type"].value)
        etlJobEnterpriseComponent.setAttributeValue("SubType", state.steps[0].controls["SubType"].value)
        let specification = {
            "Action": action,
            "Object": state.steps[0].controls["Object"].value.toUpperCase(),
            "CompanyUSID": sessionContext.getRealm(),
            "Realm": sessionContext.getRealm(),
            "EmployeeUSID": sessionContext.getEmployeeUSID(),
            "DocumentTypeUSID": documentTypeUsid || null,
            "DocumentIds": documentIds || null,
            "CleanupMode": cleanupMode,
        }
        etlJobEnterpriseComponent.setAttributeValue("Specification", btoa(JSON.stringify(specification)))
        etlJobEnterpriseComponent.executeInsert();
        setSaveBackgroundOperation(false)
        navigate("/jobs");
    }

    var funcs = {
        renderImportSubType, renderExportSubType, renderExecuteSubType,

        renderImportEnlilObject,
        renderDetermineBOMUploadSource,
        renderDetermineImportObjectDocumentType,
        renderDetermineImportDocumentTypeForm,
        renderDeterminePIImportObjectDocumentType,
        renderSelectUploadSource,
        renderUploadSpreadsheetFromLocalDisk,
        renderUploadTrainingQuizSpreadsheet,
        renderCreateTrainingQuizJobButtons,
        renderExportTrainingQuizTemplate_CreateJobButtons,
        renderImport_ImportEnlilObject_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons,

        renderExport_ExportEnlilTemplate,
        renderExport_ExportEnlilTemplate_Object_BOMTemplateDownload,
        renderExport_ExportEnlilTemplate_Object_AttachmentTemplateDownload,
        renderExportEnlilObject,
        renderExport_ExportEnlilData,
        renderExport_ExportEnlilData_CreateJobButtons,
        renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType,
        renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm,
        renderExport_ExportEnlilTemplate_Object_DetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons,

        renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType,
        renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm,
        renderExport_ExportEnlilTemplate_Object_EquipmentCalibration_DetermineObjectDocumentType,
        renderExport_ExportEnlilTemplate_Object_EquipmentPreventativeMaintenance_DetermineObjectDocumentType,
        renderExport_ExportEnlilTemplate_Object_EquipmentDetermineObjectDocumentType_DetermineExportDocumentTypeForm_CreateJobButtons,

        renderExport_ExportEnlilTemplate_Object_PIDetermineObjectDocumentType,

        renderSelectEquipmentDownloadVolume,
        renderDetermineARImportObjectDocumentType,
        renderDetermineARImportDocumentTypeForm,
        renderExportARImportDocumentTypeForm_CreateJobButtons,
        renderExecuteEnlilService,
        renderExecuteCompanyDuplication,
        renderExecuteCompanySetupSql,
        renderExecuteCopyDeltaForms,
        renderExecuteCompanyPurge,
        renderExecuteRevisionCleanup,
        renderExecutePurgeCreateJobButtons
    }

    return (
        <>
            {showUploadSpreadsheet ? <UploadSpreadsheet jobUSID={jobUSID}/> : null}
            <div style={{display: "flex", flexDirection: "column", flexGrow: 1}}>
                <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between", height: 52, backgroundColor: "#1498D8"}}>
                    <div style={{paddingTop: "9px", paddingLeft: "20px"}}>
                        <Typography style={{fontSize: 24, fontWeight: 400, color: "#FFFFFF"}}>Jobs</Typography>
                    </div>
                </div>
                <div style={{display: "flex", flexDirection: "column", padding: "15px 20px 20px 20px", width: "700px"}}>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", height: 52, backgroundColor: "#FFFFFF"}}>
                        <IconButton
                            className={css_buttons.whiteStandardButton} style={{marginLeft: "-20px", marginTop: "7px", cursor: "pointer"}} size="small"
                            onClick={() => {
                                setShowUploadSpreadsheet(true);
                            }}>
                            <KeyboardBackspace sx={{color: "#021EBB", width: "30px", height: "30px"}}/>
                        </IconButton>
                        <div style={{display: "flex", flexDirection: "row", paddingTop: "11px"}}>
                            <Typography style={{fontSize: 22, fontWeight: 500, color: "#1498D8"}}>Create Job</Typography>
                        </div>
                    </div>
                    <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", marginLeft: "0px", backgroundColor: "#FFFFFF", border: "1px solid #EDEFF3", borderRadius: "20px"}}>
                        {render()}
                    </div>
                </div>
            </div>
        </>
    );

}